/**
 * English catalogue — the default locale (D29). ICU MessageFormat.
 * Norwegian domain terms are kept where English has no equivalent (daglig leder, underenhet …).
 * Every key here must exist in nb-NO; the type system enforces it.
 */
export const en = {
  'app.name': 'Autho',
  'app.loading': 'Loading…',
  'app.error.generic': 'Something went wrong.',
  // What a person can act on, said as an action. The first link that was sent still works.
  'app.error.tooSoon':
    'A sign-in link was just sent to that address. Wait a moment before asking for another — and check your email, the first one still works.',
  // What only we can act on, said as ours.
  'app.error.notConfigured':
    'This copy of Autho was started without its sign-in service. Nobody can sign in until that is fixed; it is not something you have done.',
  // One sentence for every failure sent people to check their wifi when their session had expired. Each
  // of these has a different next step, so each gets its own (Thomas, 2026-09-25).
  'app.error.offline':
    'You appear to be offline. Nothing is wrong with Autho — it will work again when your connection does.',
  'app.error.unreachable':
    'Autho’s server is not answering. This is not your connection: the server is down, restarting, or not started.',
  'app.error.expired': 'Your session is no longer valid. Signing you out so you can sign in again.',
  'app.error.refused': 'You do not have access to that.',
  'app.error.server': 'Autho’s server hit an error. It has been logged.',
  'app.error.upstream':
    'Autho could not reach Brønnøysund. That is the register being unavailable, not your connection.',
  // A provider's refusal, said as whose it is. A 403 from Google means opposite things on either side of
  // this line, and one sentence for both is what sent a person looking for a fault of their own (F208).
  'app.error.providerNotEnabled':
    'Autho has not finished switching this provider on for itself, so there is nothing it can read yet. That is ours to fix, not yours — nothing has been read and nothing spent.',
  'app.error.reconnect':
    'The provider will not let Autho read this with the permissions this account granted. Reconnect the account and include the part that is missing.',
  'app.error.providerTooFast':
    'The provider is asking Autho to slow down. Nothing was read and nothing spent — try again in a few minutes.',
  'app.error.providerUnavailable':
    'The provider did not answer. Nothing has been read and nothing spent; try again shortly.',
  'app.error.wrongProfile':
    'This belongs to a different kind of profile from the one you have open, personal or a company’s. Switch profile and try again there.',
  'app.error.wall':
    'Autho stopped this request because it reached into another profile. Nothing was read, and it has been logged.',
  'app.error.unmarked':
    'This address is not yet marked with the kind of profile it belongs to, so Autho will not answer it. The error has been logged.',
  'app.error.wrongWords':
    'Some of these permissions belong to the other kind of profile. Grant them from that profile instead.',

  'auth.signIn.title': 'Sign in to Autho',
  'auth.signIn.google': 'Continue with Google',
  'auth.signIn.microsoft': 'Continue with Microsoft',
  'auth.signIn.provider.denied':
    'Signing in with {provider} was cancelled, so you are not signed in. Try again, or use email.',
  'auth.signIn.provider.failed':
    'Signing in with {provider} did not finish. Try again, or use email.',
  'auth.signIn.provider.unknown': 'your sign-in provider',
  'auth.signIn.provider.google': 'Google',
  'auth.signIn.provider.azure': 'Microsoft',
  'eid.vipps': 'Vipps',
  'eid.bankid': 'BankID',
  'eid.signIn': 'Continue with {provider}',
  'eid.signIn.test':
    'Vipps and BankID are in their test environments here: only test users can sign in.',
  'eid.return.working': 'Completing the sign-in with {provider}…',
  'eid.return.back': 'Back to sign in',
  'eid.return.settings': 'Back to Settings',
  'eid.linked': '{provider} is added. You can sign in with it from now on.',
  'eid.refused.noFlow': 'This sign-in was not started here, or it took too long. Start again.',
  'eid.refused.cancelled': 'The sign-in was cancelled.',
  'eid.refused.noSignUp':
    'No sign-up was waiting in this browser, or it took longer than 30 minutes. Sign up with BankID again, and open the link in the same browser.',
  'eid.refused.emailUnverified':
    'Your email address is not verified yet. Open the link in the email we sent you.',
  'eid.signUp.title': 'Finish signing up',
  'eid.signUp.verified': '{provider} verified you as {name}.',
  'eid.signUp.explain':
    'Give the email address you will sign in with. We send a link to it: open it in this browser within 30 minutes, and {provider} is added to your new account.',
  'eid.signUp.email': 'Email address',
  'eid.signUp.submit': 'Send the link',
  'eid.signUp.finishing': 'Adding BankID to your account…',
  'eid.signUp.done': 'BankID is added to your account. You can sign in with it from now on.',
  'eid.signUp.close': 'Close',
  'eid.refused.incomplete':
    'The provider did not say who you are in full: a name and a national identity number are needed.',
  'eid.refused.number': 'The national identity number the provider gave could not be verified.',
  'eid.refused.heldByAnother': 'This eID is already added to another account.',
  'eid.refused.hasAnother': 'You already have another eID of this kind. Remove it first.',
  'eid.refused.notOffered': 'Signing in this way is not offered here.',
  'eid.refused.other': 'The sign-in could not be completed. Nothing was changed.',
  'shared.title': 'Shared with companies',
  'shared.explain':
    'Let your own Autho Assistant and the agents you connected in a company read some of your Personal sources there, live. Never a message body, nothing kept in the company, every read in your Audit. Up to 90 days, and you can revoke at any time.',
  'shared.none': 'You share nothing with any company.',
  'shared.companyLeft': 'A company you no longer belong to',
  'shared.purpose': 'Purpose: {purpose}',
  'shared.until': 'until {at}',
  'shared.ended': 'ended {at}',
  'shared.expiredAt': 'expired {at}',
  'shared.revoke': 'Revoke',
  'shared.state.live': 'Shared',
  'shared.state.expired': 'Expired',
  'shared.state.revoked': 'Revoked',
  'shared.state.replaced': 'Replaced by a newer grant',
  'shared.state.membershipEnded': 'Ended: you are no longer a member',
  'shared.word.mail': 'mail headers',
  'shared.word.calendar': 'calendar events',
  'shared.word.drive': 'file names',
  'shared.noCompany': 'You belong to no company to share with.',
  'shared.noAccount': 'Connect an account in Personal first.',
  'shared.form.company': 'Company',
  'shared.form.chooseCompany': 'Choose a company',
  'shared.form.accounts': 'Accounts',
  'shared.form.words': 'What it may read',
  'shared.form.noBodies':
    'Subjects, senders and dates, events and file names. Never what a message says.',
  'shared.form.purpose': 'Purpose (kept with the grant and every answer)',
  'shared.form.days': 'Days (at most {max})',
  'shared.form.share': 'Share',
  'eid.settings.title': 'Sign in with an eID',
  'eid.settings.explain':
    'Add Vipps or BankID as a way to sign in. Autho keeps the name and birth date the provider verified, never your national identity number.',
  'eid.settings.none': 'No eID added.',
  'eid.settings.born': 'born {date}',
  'eid.settings.verified': 'verified {at}',
  'eid.settings.add': 'Add {provider}',
  'eid.settings.remove': 'Remove {provider}',
  'eid.settings.off': 'Signing in with an eID is not switched on here.',
  'auth.signIn.email.label': 'Email',
  'auth.signIn.email.submit': 'Email me a sign-in link',
  'auth.signIn.email.sent': 'Check {email} for a sign-in link.',
  'auth.signIn.providerUnavailable': '{provider} sign-in is not configured for this environment.',
  'auth.signOut': 'Sign out',

  // Who is signed in (R1). What Autho checked is the address and nothing more, so *verified* is said as *email
  // verified* and never alone: the strong check, a role at Brønnøysund plus BankID, is decided before the first
  // external user (Thomas, 2026-10-08: D83).
  'profile.signedInAs': 'Signed in as {email}',
  'profile.signedInWithoutEmail': 'Signed in without an email address',
  'profile.emailVerified': 'email verified',
  'profile.emailNotVerified': 'email not verified',
  // Every login has a Me (D81), so a sign-in that comes back with no profile at all is a fault, said as one.
  'profile.missing':
    'Autho found no profile for you — not even your own, which every sign-in has. That is a fault in Autho, not something you did.',
  // The profile switcher (R4, A7). The person's own profile is *Personal* and says it is theirs; a company is its
  // name, and says it is an organisation and, where the Org has one, its number.
  'profile.switch': 'Switch profile',
  'profile.kind.me': 'Your own profile',
  'profile.kind.orgNumbered': 'Organisation · {orgnr}',

  'tenant.orgnr.title': 'Organisation number',
  'tenant.orgnr.explain':
    "Which of this Org's companies is the Org itself? You say it once, and it cannot be changed here afterwards.",
  'tenant.orgnr.choose': 'Choose the company',
  'tenant.orgnr.set': 'This is the Org',
  'tenant.orgnr.is': 'This Org is organisation number {orgnr}.',
  // Settings no longer switches profile; it says where switching went (R4).
  'settings.profiles': 'Profiles',
  'settings.profiles.switch': 'Switch between your profiles from the top of any screen.',
  // A company is added by its organisation number, never by a name somebody types: the name comes from
  // Brønnøysund (R1, A3), and the person adding it becomes its admin (A4).
  'tenant.add': 'Add a company',
  'tenant.add.orgnr': 'Organisation number',
  'tenant.add.admin': 'You become its admin.',
  'tenant.add.submit': 'Add the company',
  // One sentence for each refusal the server names, keyed by its code. Each says what happened and, where it
  // matters, that nothing was made — a refusal comes before anything is created, because a profile is never
  // deleted. `org_exists` is about the person asking and nobody else: Autho never says whether somebody
  // else's profile for the company exists (D82, B13).
  'tenant.add.error.invalid_orgnr':
    'That is not a valid Norwegian organisation number. Check the nine digits and try again.',
  'tenant.add.error.email_unverified':
    'Only a person whose email is verified can add a company, and your email is not verified yet. Nothing was added.',
  'tenant.add.error.email_check_unavailable':
    'Autho could not find out whether your email is verified, so nothing was added. Try again in a moment.',
  'tenant.add.error.org_exists': 'You already have a profile for this company.',
  'tenant.add.error.orgnr_not_found':
    '{orgnr} is not registered in Enhetsregisteret. Check the number and try again.',
  'tenant.add.error.company_dissolved':
    'Enhetsregisteret lists {orgnr} as dissolved, so it cannot be added.',
  'tenant.add.error.provider_unavailable':
    'Brønnøysund did not answer, so the company could not be looked up. Nothing was added; try again shortly.',
  'tenant.kind.me': 'Personal',
  'tenant.kind.org': 'Organisation',

  'nav.structure': 'Structure',
  'nav.chat': 'Chat',
  'chat.title': 'Chat',
  'chat.description':
    'Ask about what this profile holds. Autho Assistant reads only what its grant allows, and every figure it gives is checked against what it read.',
  'chat.new': 'New thread',
  'chat.threads': 'Your threads',
  'chat.none': 'No threads yet. Start one with New thread.',
  'chat.untitled': 'Thread of {at}',
  'chat.spend.saved': 'Caching at the provider saved {saved}.',
  'chat.spend':
    '{calls, plural, =0 {Nothing spent in this thread yet.} one {This thread has cost {amount}, over # model call.} other {This thread has cost {amount}, over # model calls.}}',
  'chat.delete': 'Delete thread',
  'chat.deleteConfirm': 'Delete this thread and everything in it now? This cannot be undone.',
  'chat.kept':
    'Only you see your threads, here and nowhere else. Questions and answers are kept for 90 days, then deleted.',
  'chat.thread.empty': 'Nothing asked in this thread yet.',
  'chat.question': 'Your question',
  'chat.ask': 'Ask',
  'chat.how':
    'Autho Assistant answers as its own agent, under its grant on Agents. What it read is listed while it answers and kept in the audit log; what the tools returned is never kept.',
  'chat.answering': 'Answering…',
  'chat.step.read': 'Read {tool}',
  'chat.step.refused': '{tool} was refused',
  'chat.sources': 'Rests on:',
  'chat.kind.company': 'Company',
  'chat.kind.counterparty': 'Customer or supplier',
  'chat.kind.document': 'Document',
  'chat.kind.belief': 'Belief',
  'chat.kind.person': 'Person',
  'chat.kind.mail': 'Mail',
  'chat.kind.item': 'Item',
  'chat.federated.unkept':
    'Drawn from your Personal sources, under the grant you shared with this company. Not kept: it will not be here when you come back.',
  'chat.federated.stub': 'Answered from a grant; not stored.',
  'chat.federated.reask': 'Ask again',
  'chat.kind.personal': 'Your Personal sources',
  'chat.kind.ledger': 'Ledger line',
  'chat.kind.record': 'Record',
  'chat.producedBy': '{model} · {region} · {at}',
  'chat.switched': 'Switched to {model}',
  'chat.scope.label': 'Companies',
  'chat.scope.summaryAll': 'Answers from every company Autho Assistant covers.',
  'chat.scope.summaryOnly': 'Answers from {names} only.',
  'chat.scope.summaryLeaving':
    'Answers from every company Autho Assistant covers, leaving out {names}.',
  'chat.scope.gone': 'a company its grant no longer covers',
  'chat.scope.change': 'Change',
  'chat.scope.all': 'Every company Autho Assistant covers',
  'chat.scope.leaveOut': 'Leave out:',
  'chat.scope.only': 'Only these:',
  'chat.scope.save': 'Save',
  'chat.scope.cancel': 'Cancel',
  'chat.scope.none': 'Keep at least one company.',
  'chat.scope.how':
    'Later answers in this thread read only these companies. Which companies Autho Assistant covers is set by its grant on Agents.',
  'chat.switched.asked': 'switched from {from} because you asked',
  'chat.switched.numberCheck': 'switched from {from}: its answer held a figure no source supported',
  'chat.askStronger': 'Ask a stronger model ({model})',
  'chat.reason.noStronger':
    'Not answered: there is no stronger model this profile may use, so the thread keeps the one it has.',
  'chat.unchecked':
    'Not found in what Autho Assistant read: do not take it as a figure from your records.',
  'chat.uncheckedNote':
    '{count, plural, one {# figure in this answer was not found in what Autho Assistant read, and is marked.} other {# figures in this answer were not found in what Autho Assistant read, and are marked.}}',
  'chat.reason.region':
    'Not answered: this profile keeps AI processing in the EEA, and Chat’s model is not served there.',
  'chat.reason.modelChanged':
    'Not answered: this thread keeps the model that first answered it. Start a new thread.',
  'chat.reason.unavailable': 'Not answered: Chat is not switched on in this installation.',
  'chat.reason.modelFailed':
    'Not answered: the model did not answer, or today’s budget is spent. Nothing was handed to another model.',
  'chat.reason.interrupted': 'Not answered: the answer was interrupted. Ask again.',
  'chat.reason.other': 'Not answered. Ask again.',
  'nav.beliefs': 'Beliefs',
  'nav.mind': 'Mind',
  'nav.assistant': 'Assistant',
  'nav.configure': 'Configure',
  'nav.access': 'Access',
  'nav.engine': 'Engine',
  'nav.agents': 'Agents',
  'nav.approvals': 'Approvals',
  'nav.clarify': 'Clarify',
  'nav.audit': 'Audit',
  'nav.settings': 'Settings',
  // An address that is no screen (R4: `/group` among them), said in words, with the way back.
  'notFound.title': 'Nothing here',
  'notFound.body': 'There is no page at this address.',
  'notFound.home': 'Go to {screen}',

  'switcher.selectedCount': '{count, plural, one {# entity} other {# entities}}',
  // Which company a screen shows, said on the screen (F356): the switcher lists profiles only, so a company held
  // inside one is named where its records are, with the way to another, or back to the whole profile.
  'scope.inView.one': 'Showing {name} · {orgnr}.',
  'scope.inView.several': 'Showing {count, plural, one {# company} other {# companies}}.',
  'scope.inView.another': 'Open another company from Structure.',
  'scope.inView.whole': 'Show the whole profile.',

  'entity.state.managed': 'Managed',
  'entity.state.linked': 'Linked',
  'entity.state.external': 'External',
  'entity.state.managed.description': 'This organisation connects the entity’s own systems.',
  'entity.state.linked.description':
    'The entity runs its own Autho organisation and shares by grant.',
  'entity.state.external.description':
    'A holding or investment: Brønnøysund enrichment and manual valuation only.',
  'entity.state.linked.unavailable':
    'Linking needs federation, which arrives in a later milestone.',
  'entity.status.active': 'Active',
  'entity.status.dissolved': 'Dissolved',
  'entity.status.bankrupt': 'Bankrupt',
  'entity.flag.under_liquidation': 'Under liquidation',
  'entity.flag.compulsory_dissolution': 'Compulsory dissolution',
  'entity.flag.bankrupt': 'Bankruptcy registered',

  'structure.title': 'Structure',
  'structure.empty': 'No entities yet. Add one by organisation number.',
  'structure.add.title': 'Add entity',
  'structure.add.orgnr': 'Organisation number',
  'structure.add.orgnr.invalid': 'Enter a valid 9-digit organisation number.',
  'structure.add.state': 'State',
  'structure.add.submit': 'Add from Brønnøysund',
  'structure.add.notFound': '{orgnr} is not registered in Enhetsregisteret.',
  'structure.add.duplicate': '{orgnr} is already in this organisation.',
  'structure.column.name': 'Name',
  'structure.column.orgnr': 'Org. no.',
  'structure.column.form': 'Form',
  'structure.column.state': 'State',
  'structure.column.status': 'Status',
  'structure.column.synced': 'Brønnøysund',
  'structure.changeState': 'Change state',
  'structure.resync': 'Refresh from Brønnøysund',
  'structure.freshness': 'Checked {ago}',
  'structure.freshness.never': 'Never checked',
  'assets.title': 'Assets',
  'assets.intro':
    'Companies this organisation holds. Managed keeps its books here; Manual is a holding you describe by hand.',
  'assets.empty':
    'No assets yet. Mark a company above as an asset, or add one by organisation number.',
  'assets.state.managed': 'Managed',
  'assets.state.manual': 'Manual',
  'assets.state.managed.description':
    'Its books are kept in this organisation, where they already are.',
  'assets.state.manual.description': 'Described by hand: no books are kept for it here.',
  'assets.column.company': 'Company',
  'assets.column.orgnr': 'Org. no.',
  'assets.column.state': 'Kind',
  'assets.column.stake': 'Stake',
  'assets.column.notes': 'Notes',
  'assets.add.title': 'Add an asset',
  'assets.add.from': 'Company',
  'assets.add.from.new': 'A new company, by organisation number',
  'assets.add.orgnr': 'Organisation number',
  'assets.add.state': 'Kind',
  'assets.add.stake': 'Stake (% of share capital)',
  'assets.add.notes': 'Notes',
  'assets.add.submit': 'Add asset',
  'assets.edit': 'Edit',
  'assets.edit.save': 'Save',
  'assets.edit.cancel': 'Cancel',
  'assets.edit.title': 'Stake and notes for {name}',
  'assets.error.own_company':
    'This company profile is that company, so it is not one of its own assets.',
  'assets.error.asset_exists': 'That company is already an asset.',
  'assets.error.linked_later': 'A linked company cannot be an asset yet.',
  'assets.error.stake':
    'Write the stake as a per cent above 0 and at most 100, with at most two decimals.',
  'assets.error.notes_too_long': 'The notes can be at most 2,000 characters.',
  'assets.error.stake_required':
    'Say the stake: the direct share of the share capital, in per cent.',
  'assets.error.asset_ended': 'That asset has ended and is kept as history; it does not change.',
  'assets.error.invalid_date': 'Write the day the asset ended.',
  'assets.end': 'End',
  'assets.end.title': 'End {name}',
  'assets.end.reason': 'How it ended',
  'assets.end.reason.sold': 'Sold',
  'assets.end.reason.dissolved': 'Dissolved',
  'assets.end.on': 'On',
  'assets.end.submit': 'End asset',
  'assets.remove': 'Entered by mistake',
  'assets.remove.confirm':
    'Remove {name}, entered by mistake? The audit log keeps a record that it was here.',
  'assets.remove.submit': 'Remove',
  'assets.ended.title': 'Ended',
  'assets.ended.row': '{reason} on {date}',
  'register.title': 'Ownership',
  'register.source': 'Skatteetaten, per 31.12.{year}',
  'register.intro':
    'From Skatteetaten’s shareholder register: the companies that own this organisation’s companies, and what they own. Only holdings between two companies are kept. The register is per 31 December, not today, and never replaces a stake you typed.',
  'register.none': 'Not imported yet: Skatteetaten publishes the register once a year.',
  'register.ownedBy': 'Owned by',
  'register.owns': 'Owns',
  'register.nothing': 'Nothing in the register.',
  'register.allShares': 'All shares',
  'register.typed': 'You typed {stake}',
  'register.column.company': 'Company',
  'register.column.stake': 'Stake',
  'register.import': 'Import the shareholder register',
  'register.import.what':
    'Skatteetaten publishes the whole register once a year, per 31 December, as a file you order from Skatteetaten. Autho reads it here, in your browser, and sends only holdings between two companies where one of them is in this organisation’s Structure.',
  'register.import.dropped':
    'Dropped where it is read, and never sent: every row whose holder is a person; a foreign holder, which has no Norwegian organisation number and cannot be told from a person; and a number whose check digit is wrong.',
  'register.import.year': 'Register per 31 December of',
  'register.import.file': 'The register file (CSV)',
  'register.import.submit': 'Import',
  'register.import.reading': 'Reading the file…',
  'register.import.done':
    'Imported {year}: {kept, plural, one {# holding} other {# holdings}} kept for this organisation. Dropped in your browser: {person, plural, one {# row of a person} other {# rows of persons}}, {foreign, plural, one {# foreign holder} other {# foreign holders}}, {invalid, plural, one {# number with a wrong check digit} other {# numbers with a wrong check digit}}, {malformed, plural, one {# line that was not a holding} other {# lines that were not holdings}}, and {other, plural, one {# holding between other companies} other {# holdings between other companies}}.',
  'register.import.questions':
    '{count, plural, one {# stake differs} other {# stakes differ}} from what you typed. Clarify asks about each.',
  'register.error.not_a_company_row':
    'A row’s holder was not a company, so nothing was imported. Read the file again here.',
  'register.error.malformed_row':
    'A row was not a holding the register lists, so nothing was imported.',
  'register.error.duplicate_row': 'A holding was listed twice, so nothing was imported.',
  'register.error.invalid_year': 'Choose a past year from 2000.',
  'register.error.too_large': 'Too many rows at once, so nothing was imported.',
  'clarify.registerStake.question':
    'You typed {typed} for {name} ({orgnr}); Skatteetaten’s register per 31.12.{year} says {register}. Which is right?',
  'clarify.registerStake.typed': 'Keep {typed}',
  'clarify.registerStake.register': 'Use {register}',
  'clarify.asset.suggestion': 'Skatteetaten, per 31.12.{year}: {stake}',
  'clarify.asset.useSuggestion': 'Use it',
  'clarify.asset.question':
    "Is {name} ({orgnr}) one of this organisation's assets? If it is, say its direct share of the share capital.",
  'clarify.asset.yes': 'Yes, an asset',
  'clarify.asset.no': 'No, not an asset',

  'entity.section.overview': 'Overview',
  'entity.section.roles': 'Roles',
  'entity.section.subUnits': 'Sub-units (underenheter)',
  'entity.section.filedAccounts': 'Filed accounts',
  'entity.field.name': 'Name',
  'entity.field.form': 'Organisation form',
  'entity.field.nace': 'Industry code (NACE)',
  'entity.field.registeredAt': 'Registered',
  'entity.field.vatRegistered': 'VAT registered',
  'entity.field.businessAddress': 'Business address',
  'entity.field.postalAddress': 'Postal address',
  'entity.field.shareCapital': 'Share capital',
  'entity.field.employees': 'Employees',
  'entity.roles.empty': 'Brønnøysund lists no roles.',
  'entity.roles.holder': 'Holder',
  'entity.roles.role': 'Role',
  'entity.roles.deregistered': 'Deregistered',
  'entity.subUnits.empty': 'No sub-units registered.',
  'entity.accounts.empty': 'No annual accounts filed with Regnskapsregisteret.',
  'entity.accounts.latestOnly':
    'Brønnøysund’s open API returns the latest filed year only. Earlier years appear here as Autho observes them.',
  'entity.accounts.period': 'Period',
  'entity.accounts.inNok.rate':
    'The accounts to {period} were filed in {currency}; NOK beside each figure at Norges Bank’s rate for {date}: 1 {currency} = {rate}.',
  'entity.accounts.inNok.noRate':
    'The accounts to {period} were filed in {currency}. No rate: shown in {currency} only.',
  'entity.accounts.revenue': 'Operating revenue',
  'entity.accounts.operatingResult': 'Operating result',
  'entity.accounts.netResult': 'Net result',
  'entity.accounts.totalAssets': 'Total assets',
  'entity.accounts.equity': 'Equity',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.unknown': 'Unknown',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.save': 'Save',
  'common.retry': 'Retry',
  'common.loadMore': 'Load more',
  'common.export': 'Export',

  'confidence.stated': 'Stated',
  'confidence.confirmed': 'Confirmed',
  'confidence.extracted': 'Extracted',
  'confidence.inferred': 'Inferred',
  'confidence.stated.description': 'A person entered this.',
  'confidence.confirmed.description': 'Ingested, then approved by a person.',
  'confidence.extracted.description': 'Read from a source record.',
  'confidence.inferred.description': 'Derived by a model. Treat with care.',

  'provenance.title': 'Provenance',
  'provenance.open': 'Show provenance',
  'provenance.provider': 'Provider',
  'provenance.object': 'Source object',
  'provenance.observedAt': 'Observed',
  'provenance.confidence': 'Confidence',
  'provenance.lastVerified': 'Last verified',
  'provenance.override': 'Your override',
  'provenance.override.none': 'No override',
  'provenance.derivation': 'Derivation',
  'provenance.provider.brreg': 'Brønnøysundregistrene',
  'provenance.provider.manual': 'Entered in Autho',
  'provenance.provider.brreg.short': 'Brreg',
  'provenance.provider.manual.short': 'Manual',
  'provenance.provider.google': 'Google',
  'provenance.provider.google.short': 'Google',
  'provenance.provider.icloud': 'iCloud',
  'provenance.provider.icloud.short': 'iCloud',

  'engine.key.title': 'Your own model key',
  'engine.key.none': 'No key. Autho’s own models answer, under the budget above.',
  'engine.key.held':
    '{provider} key, processing in {where}, added {day}. Caps: {daily} a day, {monthly} a month.',
  'engine.key.where.us': 'the US',
  'engine.key.where.eu': 'Europe',
  'engine.key.eeaOnly':
    'This profile keeps AI processing in the EEA, so only an OpenAI key from a project in Europe can be used here.',
  'engine.key.notYetUsed': 'Nothing calls a model with it yet; Chat will.',
  'engine.key.provider': 'Provider',
  'engine.key.provider.anthropic': 'Anthropic (Console API key, processes in the US)',
  'engine.key.provider.openai': 'OpenAI (project key)',
  'engine.key.inEurope': 'The project was created with Europe as its region',
  'engine.key.key': 'Key',
  'engine.key.dailyCap': 'Daily cap (NOK)',
  'engine.key.monthlyCap': 'Monthly cap (NOK)',
  'engine.key.enter': 'Prove and keep',
  'engine.key.sealed': 'Proved with the provider and sealed. It is never shown again.',
  'engine.key.remove': 'Remove key',
  'engine.key.removed':
    'Removed here and deleted from Autho. The key still exists at the provider: delete it there.',
  'engine.key.deleteAt': 'Delete it at the provider',
  'engine.status.title': 'Status',
  'engine.status.processing': 'AI processing runs in',
  'engine.status.processing.eea': 'The EEA only',
  'engine.status.processing.any': 'Cheapest model that passes',
  'engine.status.storage': 'Your record is stored in',
  'engine.status.storage.eea': 'The EEA, under either setting',
  'engine.status.ledgers': 'Accounting connected',
  'engine.status.ledgers.none': 'Nothing connected',
  'engine.status.ledgers.count':
    '{count, plural, one {# company} other {# companies}} · {currencies}',
  'engine.status.spend': 'Spent today ({day})',
  'engine.status.spend.value': '{billed} of {limit}',
  'engine.status.documents': 'Documents',
  'engine.status.documents.value':
    '{read} read · {waiting} waiting · {failed, plural, one {# unreadable} other {# unreadable}}',
  'engine.status.queue': 'Queue',
  'engine.status.queue.clear': 'Nothing waiting',
  'engine.status.queue.waiting':
    '{count, plural, one {# document waiting} other {# documents waiting}}',
  'engine.status.reservations':
    'More than a quarter of today’s ceiling is held by calls in flight. If nothing is running, those are calls that died, and they free up at midnight UTC.',
  'engine.status.budget': 'Your budget today ({day})',
  'engine.status.budget.value': '{billed} of {limit}',
  'engine.status.meter': 'Metered this month ({month})',
  'engine.status.meter.value': '{billed}, of which Chat {chat}',
  'engine.status.sharedCeiling':
    'Two limits, and they are not the same figure. Autho’s ceiling is what this whole deployment may spend in a day; your budget is what Autho may spend on your behalf out of it. Where no budget of your own is set, the ceiling is what applies.',
  'engine.runsOn.title': 'What Autho runs on',
  'engine.runsOn.compute': 'Compute',
  'engine.runsOn.compute.value': 'Google Cloud Run (Belgium), EU',
  'engine.runsOn.database': 'Database',
  'engine.runsOn.database.value': 'Supabase Postgres (Frankfurt), EU',
  'engine.runsOn.storage': 'Storage',
  'engine.runsOn.storage.value': 'Supabase Storage (Frankfurt), EU',
  'engine.runsOn.intelligence': 'Intelligence',
  'engine.runsOn.intelligence.native.any':
    'Google Cloud’s Vertex AI, in an EU region wherever the model is offered there, and global only where it is not',
  'engine.runsOn.intelligence.native.eea': 'Google Cloud’s Vertex AI, in the EEA only',
  'engine.runsOn.intelligence.ownKey': 'Your own Anthropic or OpenAI API key',
  'engine.runsOn.inUse': 'In use',
  'engine.runsOn.chat': 'Chat’s models, cheapest first',
  'engine.runsOn.chat.tier': '{model} · {region}',
  'engine.runsOn.chat.start': 'New threads start here · {measured}',
  'engine.runsOn.chat.neverMeasured': 'never measured',
  'engine.runsOn.chat.measuredOn': 'measured on {run}',
  'engine.runsOn.notYet': 'Not offered yet',
  'engine.runsOn.subscriptions': 'Subscriptions',
  'engine.runsOn.subscriptions.value':
    'Your own assistant, such as Claude or ChatGPT, can read Autho on your own subscription.',
  'engine.runsOn.subscriptions.link': 'Connect it under Access',

  'nav.bank': 'Bank',
  'bank.title': 'Bank',
  'bank.description':
    'Balances read live from any bank you have connected, and balances from statement files you have imported. Every figure carries the date it is of and when it was read.',
  'bank.import': 'Import a statement',
  'bank.import.explain':
    'A CAMT.053 file from your bank’s online banking. Nothing is sent anywhere: it is read here and kept in your own tenant.',
  'bank.import.choose': 'Choose a file',
  'bank.import.reading': 'Reading it…',
  'bank.imported':
    'Read {statements, plural, one {# statement} other {# statements}}: {balances, plural, one {# balance} other {# balances}} and {entries, plural, one {# entry} other {# entries}}.',
  'bank.imported.again': 'Autho already had that file. Nothing changed.',
  'bank.empty': 'No statement has been imported yet.',
  'bank.balance.closing': 'Closing balance',
  'bank.balance.opening': 'Opening balance',
  'bank.balance.available': 'Available',
  'bank.balance.forward': 'Forward available',
  'bank.balance.interim': 'Interim',
  'bank.asOf': 'as of {date}',
  // How old a dated balance is, as `Intl.RelativeTimeFormat` says it: *today*, *yesterday*, *100 days ago*.
  // It said *1 days old* (Thomas, 2026-10-08).
  'bank.age': '{ago}',
  'bank.stale': 'Older than a statement period. A month may be missing.',
  'bank.from': 'From {filename}, imported {when}',
  'bank.notLive':
    'These figures come from the statement files above, not from a bank. No bank is connected, so each figure is as of the date on its statement.',
  'bank.fromFiles':
    'These figures come from statement files, not from the live feed above. A file is as of the date on it; the live feed is read when you open this page.',
  'bank.callback.title': 'Connecting the bank',
  'bank.callback.connected': 'Connected. The consent covers {accounts} accounts.',
  'bank.callback.declined': 'The bank consent was declined. Nothing was stored.',
  'bank.callback.incomplete':
    'The bank sent the browser back without a code. Start again from the Bank page.',
  'bank.validUntil': 'Valid until {date}.',
  'bank.live.title': 'Live feed',
  'bank.live.explain':
    'Accounts answered live from the bank under a consent you established. The balance is read when you open this page, and when an agent asks under a grant. Nothing is stored: a figure here was read just now and is kept nowhere.',
  'bank.live.empty': 'No bank is connected yet.',
  'bank.live.for': 'for {name}',
  'bank.account.unnamed': 'Account',
  'bank.live.reading': 'reading the balance…',
  'bank.live.noBooked': 'the bank gave no booked balance for this account',
  'bank.live.readAt': 'read at {time}',
  'bank.connect': 'Connect bank',
  'bank.connect.entity': 'Which company’s accounts',
  'bank.connect.bank': 'Bank',
  'bank.connect.start': 'Continue to the bank',
  'bank.connect.noManaged': 'No managed entity yet. Add one under Structure first.',
  'bank.connect.needsApp':
    'Paste the Enable Banking application id under Settings → Provider apps first.',
  'settings.providerApps.enablebanking': 'Enable Banking',
  'settings.providerApps.enablebanking.where':
    'From Enable Banking: Control Panel → Applications → your application. Redirect URL: https://autho.no/connect/bank/callback. There is no secret to paste — the signing key is Autho’s own.',
  'settings.providerApps.enablebanking.works':
    'Enable Banking answered: this registration works ({name}). Nothing has been read from any bank — that needs a consent, which starts on the Bank page.',
  'settings.providerApps.enablebanking.refused':
    'Enable Banking refused this registration ({status}). Either the application id is not the one they hold, or the signing key on this server belongs to a different registration. Nothing else in Autho can work until this does.',
  'settings.providerApps.enablebanking.noKey':
    'Stored, but not checked: the signing key is not on this server yet, so there was nothing to sign a request with. It arrives with the next deploy.',
  'settings.providerApps.enablebanking.unreachable':
    'Stored, but not checked: Enable Banking did not answer. Nothing is wrong with what you typed as far as Autho can tell.',
  'settings.providerApps.enablebanking.configured': 'Registered as {clientId}.',
  'settings.providerApps.applicationId': 'Application ID',
  'entity.section.books': 'Books',
  'entity.books.explain':
    'A SAF-T Financial export from the accounting system, uploaded here. It goes into the same ledger a live connection writes, so a corrected export replaces what it changes and keeps what it does not. Every upload says which period it covers.',
  'entity.books.choose': 'Upload SAF-T export',
  'entity.books.reading': 'Reading the file…',
  'entity.books.loaded':
    'Loaded: {accounts, plural, one {# account} other {# accounts}}, {vouchers, plural, one {# voucher} other {# vouchers}}, {superseded, plural, one {# record replaced} other {# records replaced}}.',
  'entity.books.again': 'Autho already had that file. Nothing changed.',
  'entity.books.empty': 'No books have been uploaded for this company.',
  'entity.books.covers': 'Covers {from} – {to}',
  'entity.books.coversUnstated': 'The file states no period',
  // **What the file holds, not only what its header claims** (Thomas, 2026-10-08): a year's file exported in
  // October states the whole year and holds postings to October.
  'entity.books.postingsTo': 'Postings up to {date}',
  'entity.books.statedPeriod': 'The file states the period {from} – {to}.',
  'entity.books.noPostings': 'The file holds no postings in that period.',
  // `{ago}` is `Intl.RelativeTimeFormat`'s: *today*, *yesterday*, *3 days ago*, with the plural right.
  'entity.books.uploaded': 'uploaded {ago}',
  // The same bytes uploaded again: said from the newest upload, while the file keeps its first one's date.
  'entity.books.uploadedAgain': 'uploaded again {ago}',
  'entity.books.from': 'From {software}, SAF-T {version}',
  'entity.books.coveredByConnected':
    'Covered by the connected accounting system, which holds these years in full. The file stays stored, and the books for its years are read from the system.',
  'entity.books.taxCodesUnknown':
    '{codes, plural, one {# VAT code} other {# VAT codes}} loaded with an unknown direction: Skatteetaten’s standard list does not place {codes, plural, one {it} other {them}}, so the lines keep the code and nothing was assumed.',
  'entity.books.doesNotClose':
    'These books do not close: the year shows {result}, and {allocated} is posted out to the allocation accounts. Both figures are from this file.',
  // **A year that has not ended cannot close yet** (Thomas, 2026-10-08): its result so far, and no warning.
  'entity.books.soFar': '{year} so far: {result}.',
  'entity.books.allocated.none': 'nothing',
  // **Said when it agrees, not only when it does not.** A check that passes in silence looks exactly like one
  // that never ran (Thomas, 2026-10-06).
  // **The crossing a person actually wants**: the books beside what the company filed, per year, with the
  // answer in a word. Two figures of the same thing from two places that cannot correct each other.
  'entity.books.filed.heading': 'Books against the accounts filed for {year}',
  'entity.books.filed.agrees':
    'Agrees with the filed accounts to the krone on {measures, plural, one {# measure} other {# measures}}. The filing is in whole kroner, so a figure under one krone apart agrees.',
  'entity.books.filed.differs': 'Does not agree with the filed accounts:',
  'entity.books.filed.difference': '{measure}: books {ours}, filed {filed}, {difference} apart.',
  'entity.books.filed.none':
    'No annual accounts are filed with Brønnøysund for {year} yet, so there is nothing to compare these books with.',
  'entity.books.filed.withheld':
    '{measures} cannot be compared: {accounts} have an opening balance Autho has not read, so the books state no total for them.',
  // D113: a year Autho holds no postings for, compared from what it can, and said in words.
  'entity.books.filed.fromYearEnd':
    'Autho holds no postings for {year}, so it is compared from the year-end balances stated on {date}, with no postings behind them.',
  'entity.books.filed.nothing':
    'Autho holds no postings for {year} and no balances stated at its end, so the year is not compared.',
  'entity.books.filed.missing':
    'Not compared, for want of data: {measures}. Autho holds no figure for {count, plural, one {it} other {them}} for this year, and a gap is not a difference.',
  'entity.books.filed.measure.totalAssets': 'Total assets',
  'entity.books.filed.measure.equity': 'Equity',
  'entity.books.filed.measure.netResult': 'Net result',
  'entity.books.filed.measure.totalLiabilities': 'Total liabilities',
  'entity.books.filed.measure.revenue': 'Revenue',
  'entity.books.filed.measure.operatingResult': 'Operating result',
  'entity.books.filed.measureHeading': 'Measure',
  'entity.books.filed.ours': 'From the books',
  'entity.books.filed.theirs': 'As filed',
  'entity.books.filed.notStated': 'not stated',
  // **The standard chart, beside the totals** (I-23; Thomas, 2026-10-19): a comparison only, never the totals.
  'entity.books.standard.heading': 'The same books by the standard chart, {year}',
  'entity.books.standard.explain':
    'A comparison only. The totals are by each account’s own number; this column places each account where the SAF-T file maps it.',
  'entity.books.standard.own': 'By its own number',
  'entity.books.standard.byStandard': 'By the standard chart',
  'entity.books.standard.account': 'Account',
  'entity.books.standard.class': 'class {digit}',
  'entity.books.standard.mapped':
    'class {digit}, {from, select, grouping_code {grouping code} other {standard account}} {code}',
  'entity.books.standard.unmapped': 'not mapped',
  'entity.books.standard.placedElsewhere':
    '{count, plural, one {# account is} other {# accounts are}} placed under another class by the file’s mapping than by {count, plural, one {its} other {their}} own number:',
  'entity.books.standard.samePlace':
    'The file’s mapping places every account in the same class as its own number does.',
  'entity.books.closes':
    'These books close: the year shows {result}, and the same amount is posted out to the allocation accounts. Both figures are from this file.',
  'entity.books.result.profit': 'a profit of {amount}',
  'entity.books.result.loss': 'a loss of {amount}',
  'entity.books.result.none': 'neither a profit nor a loss',
  // The same words in a table cell, where a result was a signed figure (Thomas, 2026-10-08).
  'entity.result.profit': 'profit {amount}',
  'entity.result.loss': 'loss {amount}',
  'entity.books.openingsUnread':
    'The balance sheet is not stated: {files} was read by an earlier version of the SAF-T reader, which did not read opening balances. Upload the same file again and they are read.',
  'entity.books.rereading':
    'The balance sheet is withheld while {files} is read again by the current version of the SAF-T reader. Autho kept the file, so this happens by itself within the hour.',
  'entity.books.stoppedAgreeing.upload':
    'The {year} books agreed with the accounts filed for {year} until {file} was uploaded on {when}. They no longer agree: the comparison below shows where.',
  'entity.books.stoppedAgreeing.reread':
    'The {year} books agreed with the accounts filed for {year} until {file} was read again on {when}. They no longer agree: the comparison below shows where.',
  'entity.books.stoppedAgreeing.unnamedFile': 'a file',
  'entity.books.openingsRead':
    'Opening balances: {written, plural, one {# read from the file, and it agrees} other {# read from the file, and every one of them agrees}} with the file’s own closing balance.',
  'entity.books.openingsDisagree':
    'Opening balances: {written, number} read, and {count, plural, one {# account does} other {# accounts do}} not add up against this file’s own closing balance — {accounts}. The books are loaded; these figures are the file’s.',
  'entity.books.openingsMidYear':
    'This file starts mid-year, so the balances it states are as at its own start date rather than the year’s. They were checked against the file’s own closing balances and not stored as opening balances.',
  'entity.books.openingsPlacedMidYear':
    'This file starts mid-year and is the first of this company’s books, so the balances it states at its start were placed as the balances on {on}. A file covering the months before replaces them.',
  'entity.books.openingsMissing':
    'No opening balance in this file for {accounts}. Totals that need them are not shown rather than shown as a movement.',
  // D112: a 1 January file's result-account openings are last year's closing figures, never carried into the year.
  'entity.books.openingsLastYearsResult':
    '{count, plural, one {# result account opens} other {# result accounts open}} the year with last year’s closing figure ({accounts}). A result account starts every year at nothing, so {count, plural, one {it is} other {they are}} not carried into this year, and {count, plural, one {counts} other {count}} in equity until last year is closed.',
  'nav.inbox': 'Inbox',
  'inbox.title': 'Inbox',
  'inbox.description':
    'What is past its date, what nobody has answered, what you promised, and who you have gone quiet on. Counted from your own headers by rules — no model reads your mail.',
  // Empty is a real answer and reads like one: this is the state you want to be in.
  'inbox.empty': 'Nothing needs you today.',
  'inbox.empty.explain':
    'Nothing is overdue, nothing is waiting on an answer, and nobody has gone quiet. Autho will say so here when that changes.',
  'inbox.empty.nothingConnected':
    'Nothing is connected yet, so there is nothing to look at. Connect an account and Autho will start telling you what needs you.',
  'inbox.connect': 'Connect an account',
  'inbox.kind.overdue': 'Late',
  'inbox.kind.unanswered_ask': 'Unanswered',
  'inbox.kind.due_soon': 'Due soon',
  'inbox.kind.promise_open': 'You promised',
  'inbox.kind.gone_quiet': 'Gone quiet',
  'inbox.days.overdue': '{days} days past the date',
  'inbox.days.today': 'due today',
  'inbox.days.unanswered': 'asked {days} days ago, nothing written back',
  'inbox.days.due': 'due {date}',
  'inbox.days.promise': 'promised {days} days ago, still open',
  'inbox.days.quiet': 'last heard from {days} days ago',
  'inbox.rule': 'raised by {rule}',
  'inbox.open': 'Open it',
  'inbox.snooze': 'Not now',
  // The end date, said out loud. A suppression whose end nobody can see is a silence.
  'inbox.unsnooze': 'Tell me again',
  'inbox.snooze.explain':
    'This hides the line for a month. It says nothing about them: the rhythm, the count and everything else stay exactly as they were.',
  // Said on every visit, not only on the click that did it: a suppression nobody can see afterwards is
  // the silence this was written to avoid.
  // The empty state has two sentences, because it has two meanings. The first says nothing is late and
  // nobody has gone quiet; the second must not, because somebody has and it is on the line above.
  'inbox.empty.explain.snoozed':
    'Nothing is overdue and nothing is waiting on an answer. What has gone quiet is put off, above.',
  'inbox.snoozed.one': '{name} has gone quiet and is put off until {date}.',
  'inbox.howMade':
    'Every line here is a count or a date: nobody wrote back, the date has passed, this is twice as long as usual. Whether a promise was kept is in the message itself, which Autho does not hold.',
  // *Older* (F516): what has been on the list more than a month, kept and one press from today's list.
  'inbox.view': 'Which lines',
  'inbox.view.today': 'Today',
  'inbox.view.older': 'Older',
  'inbox.older.explain':
    'Lines that have been on the list more than {days} days. Nothing about them has changed: each is kept, can be found here, and comes back to today’s list with one press.',
  'inbox.older.search': 'Search older lines',
  'inbox.older.empty': 'Nothing has been on the list long enough to be here.',
  'inbox.older.noMatch': 'No older line matches.',
  'inbox.older.bringBack': 'Bring back',
  'inbox.older.bringBack.explain':
    'Puts the line back on today’s list for another {days} days. Nothing else changes.',
  'inbox.older.note':
    'A line on the list more than {days} days moves to Older by itself, where it is kept and can be brought back.',
  'nav.relationships': 'Relationships',
  'relationships.title': 'Relationships',
  'relationships.description':
    'The people in your mail and calendar, counted from the headers: who wrote, who was invited, how often, when last. No model reads your mail, and nothing is looked up anywhere else.',
  'relationships.search': 'Find a person or an organisation',
  'relationships.empty': 'Nobody yet. People appear here after a connected account has been read.',
  'relationships.person.counts': '{from} from them · {to} to them · {meetings} meetings',
  'relationships.person.cadence': 'about every {days} days',
  'relationships.person.lastSeen': 'last {date}',
  'relationships.person.basis': 'from {count} headers',
  'relationships.person.strength': 'Strength of the tie, from counts and dates',
  'relationships.commitments': 'Commitments',
  'relationships.commitments.explain':
    'What a subject line or an invitation says has been agreed to. The kind, the direction and a date where one was written — what was actually agreed is in the message, which Autho does not hold.',
  'relationships.commitments.empty': 'Nothing open.',
  'relationships.kind.meeting': 'Meeting',
  'relationships.kind.ask': 'Asked of you',
  'relationships.kind.promise': 'You promised',
  'relationships.kind.payment_due': 'Payment due',
  'relationships.direction.mine': 'yours to do',
  'relationships.direction.theirs': 'theirs to do',
  'relationships.due': 'due {date}',
  'relationships.undated': 'no date written',
  'relationships.rule': 'read by the rule “{rule}”',
  'relationships.done': 'Done',
  // The same word Beliefs uses for the same act, in both languages, because these records will one day be
  // beliefs (O50) and a synonym now is a migration of user-visible history later.
  'relationships.forget': 'Forget',
  'relationships.forgotten': 'Forgotten',
  'relationships.forgetIsNotErase':
    'Forgetting stops a commitment being used, at every point in time. It does not rewrite history: the record stays, and so does the audit trail.',
  'relationships.showForgotten': 'Show forgotten',
  'relationships.people.recomputed':
    'Nothing here is corrected: every figure is a count, recomputed from your headers after each sync. What can be forgotten is a commitment.',
  'relationships.proposals': 'Possibly the same person ({count})',
  'relationships.proposals.explain':
    'Cards with the same full name. Autho never joins two addresses by itself: say whether each pair is one person.',
  'relationships.proposals.pair': '{a} and {b}',
  'relationships.proposals.mergeAll': 'Same person, all {count}',
  'relationships.samePerson': 'Same person',
  'relationships.notSame': 'Not the same',
  'relationships.notThisPerson': 'Not this person',
  'relationships.notThisPerson.label': '{email} is not this person',
  'relationships.how':
    'Everything on this screen was counted from headers by rules — an address, a name, a date, a subject line. Nothing here was read from a message body, and no model was asked.',
  'nav.conversations': 'Conversations',
  'nav.calendar': 'Calendar',
  'nav.files': 'Files',
  'record.conversations.title': 'Conversations',
  'record.conversations.description':
    'The mail Autho holds, newest first: who sent it, the subject, the date and the snippet the provider gives.',
  'record.calendar.title': 'Calendar',
  'record.calendar.description':
    'Every event Autho holds, from every calendar you connected, in one list: today and what is coming first, then what has been.',
  'record.calendar.earlier': 'Before today',
  'record.files.title': 'Files',
  'record.files.description':
    'The files Autho knows about, newest first. Autho keeps a pointer to each file, never the file itself.',
  'record.fedBy': 'Fed by',
  'record.fedBy.manage': 'Manage under Sources',
  'record.fedBy.paused': 'Paused',
  'record.fedBy.notApproved': 'First read not approved yet',
  'record.connect.mail': 'Connect a Google mailbox',
  'record.connect.calendar': 'Connect a Google calendar',
  'record.connect.drive': 'Connect Google Drive',
  'record.icloud': 'iCloud is connected under Sources, with an app-specific password.',
  'record.icloudDrive':
    'iCloud Drive cannot be connected: Apple offers no way for another service to read it.',
  'record.conversations.unfed':
    'Nothing feeds Conversations yet. Connect a mailbox and its mail is listed here.',
  'record.calendar.unfed':
    'Nothing feeds Calendar yet. Connect a calendar and its events are listed here.',
  'record.files.unfed':
    'Nothing feeds Files yet. Connect Google Drive and its files are listed here.',
  'record.empty.unread':
    'Nothing here yet: an account is connected but its first read has not been approved. Approve it under Sources.',
  'record.empty.nothing': 'Nothing here yet from the accounts that feed this page.',
  'record.account': 'Account',
  'record.account.all': 'All accounts',
  'record.column.when': 'Date',
  'record.column.from': 'From',
  'record.column.subject': 'Subject',
  'record.column.event': 'Event',
  'record.column.organizer': 'Organiser',
  'record.column.name': 'Name',
  'record.column.type': 'Type',
  'record.column.account': 'Account',
  'record.column.open': 'At the provider',
  'record.allDay': 'All day',
  'record.noSubject': '(no subject)',
  'record.untitled': '(untitled)',
  'record.open': 'Open at {provider}',
  'record.more': 'Show more',
  'record.file.document': 'Document',
  'record.file.spreadsheet': 'Spreadsheet',
  'record.file.presentation': 'Presentation',
  'record.file.pdf': 'PDF',
  'record.file.image': 'Image',
  'record.file.folder': 'Folder',
  'record.file.other': 'File',
  'record.how.mail':
    'Only the sender, subject, date and the snippet the provider gives are shown here. The message itself opens at the provider.',
  'record.how.calendar':
    'Read from your calendars as they were at the last read; nothing here is ever written back to a calendar.',
  'record.how.drive':
    'Autho holds the name, type and date of each file and where it is; the file opens at the provider.',
  'accounts.reading.started': 'Reading this account now. It carries on in the background.',
  'accounts.reading.soFar':
    'Reading this account now — {held} found so far. It carries on in the background; you can leave this page.',
  'accounts.reading.ofAbout': '{held} of about {expected} read — {percent}% of the way through.',
  'accounts.reading.ofAboutLeft':
    '{held} of about {expected} read — {percent}% of the way through, roughly {minutes} min left.',
  'accounts.reading.stalled':
    'Stalled since {when}. Nothing has been read since then. Autho tries again every hour, and I am told when a read stops; nothing is lost and nothing is charged twice when it starts again.',
  'accounts.held': '{held} things held from this account.',
  /**
   * **A failed withdrawal, on the screen that lists the account** (Thomas, 5 October). Autho's copy is gone;
   * the provider's may not be, and the only person who can finish that is the owner. Two revocations have
   * failed silently now, so the rule is that one never passes in silence again. Said on a forgotten account
   * too, and in so many words: the access may still be live, and here is where to remove it (D99).
   */
  'accounts.revokeFailed':
    'Autho could not withdraw its access at {provider}, so it may still be live there. Remove Autho under the integrations in your {provider} settings.',
  /**
   * **Moving a personal account out of a company's profile** (R2; D87, D92). Shown on the company's Connected
   * accounts, on each Google or iCloud account still connected there, to the person who connected it.
   */
  'accounts.move.title': 'Move to your personal profile',
  'accounts.move.explain':
    'This is a personal account in a company’s profile. Moving it copies it to your personal profile with everything you decided about it, checks that it can be read there, and only then lets you remove it here. Nothing is withdrawn at {provider}.',
  'accounts.move.stage.notStarted': 'Not moved yet.',
  'accounts.move.stage.interrupted':
    'A move stopped before it finished. Continue it, or cancel it.',
  'accounts.move.stage.copying':
    'Copying to your personal profile: {held} of {total} so far. Continue to carry on. Nothing has been removed here.',
  'accounts.move.stage.copied':
    'Copied to your personal profile, but Autho has not yet read the account there. Nothing has been removed here.',
  'accounts.move.stage.waiting':
    'Copied and read in your personal profile. Waiting for {agents} to read it there before this copy can be removed.',
  'accounts.move.stage.ready':
    'Copied and read in your personal profile. You can now remove it from this company’s profile.',
  'accounts.move.stage.already':
    'Your personal profile already has this account, connected there. Moving it adds what this company’s copy knows that the connection there does not, and keeps everything that is its own: what it has read, what you decided there and its approval. Then you can remove it here. Nothing is withdrawn at {provider}.',
  'accounts.move.stage.done': 'Moved to your personal profile on {when}.',
  'accounts.move.access': 'Set up in your personal profile as well:',
  'accounts.move.access.item': '{agent} may read {resources} until {when} — “{purpose}”',
  'accounts.move.access.none': 'No agent reads this account here, so there is no access to set up.',
  'accounts.move.counted':
    'Counted in both profiles: {items} items, {calendars} calendars, {addresses} addresses.',
  'accounts.move.start': 'Move to my personal profile',
  'accounts.move.continue': 'Continue the move',
  'accounts.move.working': 'Moving… this can take a few minutes.',
  'accounts.move.finish': 'Remove it from this company',
  'accounts.move.finish.explain':
    'This removes the company’s copy and everything read into it here. The copy in your personal profile stays, and nothing is withdrawn at {provider}.',
  'accounts.move.finish.confirm': 'Yes, remove it here',
  'accounts.move.cancel': 'Cancel the move',
  'accounts.move.stopped.incomplete':
    'There was more than one press can copy. Everything copied so far is kept; continue to carry on from there.',
  'accounts.move.stopped.counts':
    'The copy did not match when it was counted, so nothing was removed. Continue to copy it again.',
  'accounts.move.stopped.inconclusive':
    'The count could not see the account in both profiles, so nothing was removed. Continue to try again.',
  'accounts.move.stopped.proof':
    'Autho could not read the account with the copy in your personal profile, so nothing was removed. Continue to try again.',
  'accounts.move.blocked.handshake':
    'A connection is being made in this profile right now. Try again in a few minutes.',
  'accounts.move.blocked.failing':
    'Autho cannot read the account with the copy in your personal profile right now, so this copy stays until it can.',
  // F352: one account in two companies. The move's progress is Personal's connection's, not a company's, so it is
  // refused from every company until it can tell them apart. Said plainly, with what to expect: nothing changed.
  'accounts.move.blocked.inAnotherCompany':
    'This account belongs to more than one of your companies: it is also connected in another one, or it was moved to your personal profile from another one. It cannot be moved from here yet.',
  // **A press the server refused**, by its code (F336): said in the reader's own language rather than in the
  // API's English sentence. Most often a second tab: a cancel pressed after the finish, a finish after a cancel.
  'accounts.move.refused.notAnOrg':
    'Only a personal account in a company’s profile can be moved to a personal profile.',
  'accounts.move.refused.notPersonal':
    'Only Google and iCloud accounts are personal. This connection is the company’s own and stays here.',
  'accounts.move.refused.notYours':
    'Only the person who connected this account can move it, because it moves to their own profile.',
  'accounts.move.refused.notLive':
    'This account is no longer connected here, so there is nothing to move.',
  'accounts.move.refused.noIdentity':
    'Nothing identifies the account behind this connection, so it cannot be found again in your personal profile.',
  'accounts.move.refused.noCredential':
    'This connection holds no credential, so a copy could never read the account. Connect it in your personal profile instead.',
  'accounts.move.refused.alreadyInPersonal':
    'Your personal profile already has this account in a connection that is no longer live there, with what was read kept. Reconnect it in your personal profile and the move adds to that connection, or forget it there first.',
  'accounts.move.refused.alreadyProven':
    'The copy in your personal profile has been read already, so it is not copied over again.',
  'accounts.move.refused.handshakeOpen':
    'A connection to this provider is being made in this profile right now. Wait a few minutes and try again.',
  'accounts.move.refused.accessChanged':
    'Which agents read this account changed since the page was loaded. Look at the list again and press once more.',
  'accounts.move.refused.notStarted':
    'There is no copy in your personal profile for this move: nothing has been copied yet, or the move was cancelled. Nothing was removed here.',
  'accounts.move.refused.notProven':
    'Autho has not yet read the account with the copy in your personal profile, so this copy cannot be removed.',
  'accounts.move.refused.waitingForAgent':
    'An agent that reads this account here has not yet read it in your personal profile, so this copy cannot be removed.',
  'accounts.move.refused.personalFailing':
    'Autho cannot read the account with the copy in your personal profile right now, so this copy cannot be removed.',
  'accounts.move.refused.noOtherCopy':
    'No other copy of this account is connected, so removing this one would remove the only one.',
  'accounts.move.refused.alreadyFinished':
    'The move has already finished, so there is nothing left to cancel. The account is in your personal profile.',
  'accounts.move.refused.nothingToCancel': 'There is no move in progress to cancel.',
  'accounts.move.refused.inAnotherCompany':
    'This account belongs to more than one of your companies, so it cannot be moved from here yet. Nothing has been changed.',
  // **The calendar picker** (F303). A Google account lists everything the person has ever subscribed to,
  // and one of Thomas's is a company's team calendar shared into a personal account. The three words have to
  // say what happens to the *events*, not what happens to the calendar, because that is the decision.
  'accounts.todoist': 'Alerts to your list',
  'accounts.todoist.explain':
    'When something goes wrong — a read failing, a deploy failing — Autho adds one task to your Todoist. The grant only lets Autho add tasks: it cannot read, complete or delete anything there, and a wider token is refused and revoked on the spot.',
  'accounts.todoist.connect': 'Connect Todoist',
  'todoist.callback.title': 'Connecting Todoist',
  'todoist.callback.connected':
    'Connected. Autho can add a task to your list, and nothing else. The first real incident becomes one task; a repeat of the same incident does not become a second.',
  /**
   * **It used to send him to a setting that does not exist.** Todoist takes the scope from the link Autho
   * sends — the app page there has a name, an icon, a redirect URL and nothing about scopes — so *check the
   * app settings at Todoist* was an instruction nobody could carry out. What is true is that this is Autho's
   * to fix, and the sentence says so.
   */
  'todoist.callback.tooWide':
    'Todoist handed back a token that can read, when Autho asked only to add tasks. Nothing was stored. There is nothing for you to change at Todoist — the scope comes from the link Autho sends, so this is Autho’s to fix.',
  'todoist.callback.unverified':
    'Autho could not establish what this token can do: the check answered {status}. Nothing was stored, because a token Autho cannot vouch for is not kept.',
  /** A separate sentence, because *answered 0* is not a thing that happened and would read as a status. */
  'todoist.callback.unverifiedSilent':
    'Autho could not establish what this token can do: the check got no answer at all. Nothing was stored, because a token Autho cannot vouch for is not kept.',
  /**
   * The one step left when Autho could not withdraw the token itself. Said plainly, with where to go: the
   * alternative is what happened on 5 October, when the screen claimed a revocation that never landed.
   */
  'todoist.callback.notRevoked':
    'Autho could not withdraw the token at Todoist. Remove Autho there yourself: Settings → Integrations → Connected apps.',
  'todoist.callback.unproven':
    'Connected, but the first task could not be added, so the path is not proved yet. Nothing is wrong with the connection itself — Autho will try again the next time something needs you, and this is written in the audit log.',
  'todoist.callback.declined': 'Nothing was connected. You can try again from Connected accounts.',
  'accounts.calendars': 'Calendars in this account',
  'accounts.calendars.explain':
    'An account lists every calendar you have ever subscribed to, including other people’s. Autho reads your own calendar in full, and from the others only the events you are on. Change any of them here.',
  'accounts.calendars.none':
    'No calendars listed yet. They appear the first time Autho reads this account’s calendar.',
  'accounts.calendars.primary': 'Your own calendar',
  'accounts.calendars.choice.read': 'Everything in it',
  'accounts.calendars.choice.attendees': 'Only events I am on',
  'accounts.calendars.choice.ignore': 'Nothing',
  'accounts.calendars.default': 'Not chosen — Autho is doing this by default',
  'accounts.calendars.decided': 'You chose this on {when}',
  'accounts.calendars.show': 'Calendars ({count})',
  // **The row answers the thumb** (F310). Four one-word states under the row that was clicked; the apply
  // line is the one that stops answers being silently half-updated while stored events catch up.
  'accounts.calendars.saving': 'Saving…',
  'accounts.calendars.savedNote': 'Saved',
  'accounts.calendars.applying': 'Applying to what’s already stored…',
  'accounts.calendars.failed':
    'The save failed, so the stored choice is shown again. Try once more.',
  'accounts.calendars.hide': 'Hide calendars',
  // **Which addresses are you** (F307). The words have to make the stakes plain without alarming anybody:
  // an address listed here decides which events count as the person's own, and nothing else.
  'accounts.addresses.show': 'Your addresses ({count})',
  'accounts.addresses.hide': 'Hide addresses',
  'accounts.addresses': 'Which addresses are you?',
  'accounts.addresses.explain':
    'Autho counts an event as yours when one of these addresses is on it. The address this account signs in as is always you. Add any other address you are invited by — a work address on another domain, for instance.',
  'accounts.addresses.account': 'This account — always you',
  'accounts.addresses.suggested':
    'Seen on a calendar in this account. Autho does not treat it as you until you say so.',
  'accounts.addresses.yes': 'Yes, that is me',
  'accounts.addresses.no': 'Not me',
  'accounts.addresses.notMine': 'You said this is not you',
  'accounts.addresses.none': 'No other addresses yet.',
  'accounts.addresses.add': 'Add',
  'accounts.addresses.failed':
    'That answer did not save, so the address is back where it was. Try once more.',
  'accounts.addresses.addLabel': 'Another address you are invited by',
  // **Sign in with Autho** (F494, F508): an app of Autho's own asks who the person is. It learns the account's id and
  // nothing else, and the screen says so before the button.
  'signInWithAutho.title': 'Sign in with Autho',
  'signInWithAutho.asks': '{name} wants to sign you in with your Autho account.',
  'signInWithAutho.gets':
    'It learns an ID for you that only this app is given, and nothing else: none of your data, and no way into any profile.',
  'signInWithAutho.every': 'Autho asks each time, and keeps a record in your Personal profile.',
  'signInWithAutho.again': '{name} asks you to sign in to Autho again first.',
  'signInWithAutho.signInAgain': 'Sign in again',
  'signInWithAutho.allow': 'Sign in',
  'signInWithAutho.deny': 'Don’t sign in',
  'signInWithAutho.gone':
    'This sign-in has ended, or was started in another browser. Start it again from the app.',
  'signInWithAutho.failed': 'That did not work. Start the sign-in again from the app.',
  // **The consent screen** (O16). The one page an outside program sends a person to; every sentence is
  // weighed against somebody tapping through it on a phone. The name-is-a-claim line is the load-bearing one.
  'connectAgent.title': 'Connect an assistant',
  'connectAgent.description':
    'A program is asking to read what your agents may read — in your Personal profile, in one company, or both. Nothing is connected until you approve.',
  'connectAgent.broken':
    'This request is incomplete ({missing}), so there is nothing safe to approve. Close this page and start again from the program that sent you here.',
  'connectAgent.redirectRefused':
    'The program asked to be answered at an address it never registered. That is the shape interception takes, so Autho will not answer it at all.',
  'connectAgent.asks': '{name} is asking to connect.',
  'connectAgent.nameIsAClaim':
    'The name is what the program said about itself — Autho cannot verify it. If you were not expecting this, choose Deny.',
  'connectAgent.codeGoesTo': 'If you approve, the sign-in code is sent to',
  'connectAgent.hostIsAFact':
    'The name above is a claim. This address is not: Autho refuses any address the program did not register.',
  // One connection per person (D85): a profile at a time, Personal first, and at most one company.
  'connectAgent.choose': 'Which agent, in which profile?',
  'connectAgent.grantsRule':
    'In each profile, the program will hold exactly what the agent you choose there holds — its grants, decided on that profile’s Agents screen. Approving here grants nothing new.',
  'connectAgent.none': 'None — nothing from this profile',
  'connectAgent.oneCompany':
    'One company per connection. Choosing an agent in one company clears the others.',
  'connectAgent.notAdmin': 'Only an owner or admin of this profile can connect its agents.',
  'connectAgent.noAgents':
    'No agent here yet. Create one on this profile’s Agents screen and give it a grant first.',
  'connectAgent.chooseOne': 'Choose an agent in at least one profile, or deny.',
  'connectAgent.approve': 'Approve',
  'connectAgent.deny': 'Deny',
  'nav.sources': 'Sources',
  'accounts.title': 'Sources',
  'accounts.description':
    'Everything Autho reads from, one row each: what it feeds, what it may read, and when it last did.',
  'sources.feeds': 'Feeds: {feeds}',
  'sources.feeds.none': 'Feeds nothing in Mind',
  'sources.feed.structure': 'Structure',
  'sources.feed.books': 'Books',
  'sources.feed.bank': 'Bank',
  'sources.feed.conversations': 'Conversations',
  'sources.feed.calendar': 'Calendar',
  'sources.feed.files': 'Files',
  'sources.feed.relationships': 'Relationships',
  'sources.feed.alerts': 'Alerts, sent and never read',
  'sources.feed.goals': 'Goals, read when you open them',
  'sources.paused': 'Paused since {when}',
  'sources.pause': 'Pause',
  'sources.resume': 'Resume',
  'sources.pause.explain': 'Autho stops reading; nothing is changed at the provider.',
  'sources.resume.explain': 'Resuming reads again from where it stopped.',
  'sources.others': 'Registers',
  'sources.pausedHere':
    'A source of this page is paused, so nothing new arrives here until it is resumed under Sources.',
  'accounts.connect': 'Connect a Google account',
  'accounts.connect.explain':
    'One thing at a time, so you can see what you are agreeing to. Read-only: Autho can never send, change or delete anything.',
  'accounts.surface.mail': 'Mail',
  'accounts.surface.calendar': 'Calendar',
  'accounts.surface.drive': 'Files',
  'accounts.surface.contacts': 'Contacts',
  'card.description':
    'One person: what their mail and contacts say about them, and what they are part of.',
  'card.back': 'All relationships',
  'card.who': 'Who',
  'card.untitled': '(untitled)',
  'card.contact': 'From your contacts',
  'card.contact.notConnected': 'Your contacts are not connected. Connect them from Relationships.',
  'card.contact.failed': 'Your contacts could not be read just now. Nothing has been changed.',
  'card.contact.none': 'Not in your contacts.',
  'card.contact.source': 'From Google Contacts, read just now',
  'card.goals': 'Goals',
  'card.goals.none': 'No goal is linked to anything with this person.',
  'card.commitments': 'Commitments',
  'card.commitments.none': 'Nothing open names this person.',
  'card.events.none': 'No event with this person.',
  'card.conversations.none': 'No mail with this person.',
  'relationships.contacts': 'In your contacts, no conversation yet',
  'relationships.contacts.explain':
    'Read from your Google Contacts when this page opens, and never stored. A contact enriches the person it shares an address with; one who shares none is listed here, and never made into a person.',
  'relationships.contacts.notOffered':
    'Google Contacts cannot be connected yet: it is waiting to be added to Autho’s Google consent screen. People here come from your mail and calendar meanwhile.',
  'relationships.contacts.notConnected': 'Your contacts are not connected.',
  'relationships.contacts.connect': 'Connect Google Contacts',
  'relationships.contacts.failed':
    'Your contacts could not be read just now. Nothing has been changed.',
  'relationships.contacts.none': 'Everyone in your contacts is someone you have written with.',
  'accounts.empty': 'Nothing connected yet.',
  // In an Org, where Google and iCloud are no longer offered (R4, D84): where they went, in words.
  'accounts.personalElsewhere':
    'Google and iCloud accounts are personal, and are connected in your own profile, Personal.',
  'accounts.state.active': 'Active',
  'accounts.state.expiring': 'Expiring',
  'accounts.state.stale': 'Needs reconnecting',
  'accounts.state.revoked': 'Disconnected',
  'accounts.state.forgotten': 'Forgotten',
  // Says what it is and when, and nothing that could be read as access: there is nothing left to read
  // with, and nothing left to do to this row.
  'accounts.forgotten.tombstone':
    'A {provider} account was connected here and everything Autho had read from it was erased on {when}. Nothing of it remains; this line is kept because the audit log refers to it.',
  'accounts.lastSync': 'Last read {when}',
  'accounts.never': 'never',
  'accounts.scope.calendar': 'read your calendar',
  'accounts.scope.contacts': 'read your contacts',
  'accounts.scope.drive': 'read your files in Drive',
  'accounts.scope.email': 'see your email address',
  'accounts.scope.identity': 'confirm who you are',
  'accounts.scope.mail': 'read your mail',
  'accounts.scope.tasksAdd': 'add tasks to your list',
  'accounts.scope.tasksRead': 'read your tasks',
  'accounts.scopes': 'Autho may {scopes}.',
  'accounts.scopes.none': 'Nothing granted yet.',
  'accounts.expiring.note': 'Access ends {when}. Reconnect before then and nothing stops.',
  'accounts.stale.note': 'Autho cannot read this account. Nothing has been read since it stopped.',
  'accounts.reconnect': 'Reconnect',
  'accounts.disconnect': 'Disconnect',
  'accounts.disconnect.title': 'Disconnect this account?',
  'accounts.disconnect.explain':
    'Autho deletes its access and stops reading. If this account is not connected anywhere else, Autho also withdraws the permission at Google.',
  'accounts.basis': 'Why (kept in the log)',
  'accounts.disconnect.confirm': 'Disconnect',
  'accounts.cancel': 'Cancel',
  'accounts.icloud': 'Connect an iCloud account',
  'accounts.icloud.explain':
    'Apple offers no sign-in for Mail and Calendar, so this needs an app-specific password. Create one at appleid.apple.com under Sign-In and Security, and paste it here. Autho stores it encrypted and never shows it again.',
  'accounts.icloud.address': 'Your iCloud email address',
  'accounts.icloud.password': 'App-specific password',
  'accounts.icloud.connect': 'Connect',
  'accounts.icloud.checking': 'Checking it with Apple…',
  'accounts.icloud.refused.mail':
    'Apple refused the password for Mail. Check the address, and generate a new password if you are unsure.',
  'accounts.icloud.refused.calendar':
    'Apple accepted the password for Mail but refused it for Calendar. Generate a new one and try again.',
  'accounts.icloud.contacts': 'Also read Contacts (names and addresses, for who-is-who)',
  'accounts.icloud.contacts.works':
    'Contacts answered: {books} address book(s), {cards} card(s). Reading them starts with a release only you can deploy.',
  'accounts.icloud.connected': 'Connected. Mail and Calendar can be read.',
  'accounts.icloud.noDrive':
    'iCloud Drive is not included: Apple offers no way for another program to read it.',
  'accounts.icloud.withdraw':
    'Disconnecting deletes the password Autho holds. Apple has no way for Autho to withdraw it, so withdraw it yourself at appleid.apple.com to stop it working anywhere else.',
  'accounts.notRead': 'Nothing has been read from this account yet.',
  'accounts.estimate': 'What would the first read cost?',
  'accounts.estimate.counting': 'Counting what is there…',
  'accounts.estimate.items': 'About {items} things to read, at roughly {chars} characters each.',
  'accounts.estimate.atLeast':
    'At least that many: Calendar and Drive do not say how many there are in total, so the real number may be higher.',
  'accounts.estimate.cost': 'Estimated cost: {nok}.',
  'accounts.estimate.budget': '{remaining} left of today’s {limit}.',
  'accounts.estimate.inherited':
    'No budget of your own is set, so Autho’s daily limit for the whole installation is what applies.',
  'accounts.estimate.wouldDefer':
    'That is more than today’s budget allows, so Autho reads it over several days rather than stopping.',
  'accounts.estimate.surface': '{surface}: {items}',
  'accounts.start': 'Start reading',
  'accounts.started': 'Reading starts at the next sync.',
  'accounts.forget': 'Also erase everything Autho read from this account',
  'accounts.forget.explain':
    'The messages, events and files Autho kept a pointer to, and what it learned from them. This cannot be undone. Disconnecting on its own stops Autho reading and keeps what it already read.',
  'accounts.forgotten': 'Erased {items} things Autho had read from this account.',
  'accounts.disconnected': 'Disconnected. What Autho already read is still here.',

  'connect.title': 'Connecting',
  'connect.done': 'Connected. Autho will start reading at the next sync.',
  'connect.back': 'Back to connected accounts',
  'connect.declined': 'Nothing was connected, and nothing was changed.',
  'connect.lost':
    'This did not finish — the sign-in ended while you were on Google’s page. Nothing was connected. Start again and it will take a moment.',
  'connect.outcome.replayed':
    'That link had already been used. Nothing was connected — start again.',
  'connect.outcome.expired':
    'This took long enough that the link expired. Nothing was connected — start again.',
  'connect.outcome.unknownState':
    'This did not come from a connection Autho started, so nothing was connected.',
  'connect.outcome.wrongPerson':
    'That connection was started by someone else signed in on this computer. Nothing was connected.',
  'connect.outcome.missingScopes':
    'Autho still needs access to: {detail}. Start again, and leave the boxes ticked.',
  'connect.outcome.differentAccount':
    'That is a different Google account from the one you were reconnecting. Nothing was changed.',
  'connect.outcome.noRefreshToken':
    'Google did not give Autho lasting access, so Autho would stop reading within the hour. Nothing was connected. Remove Autho under your Google account’s third-party access and connect once more.',
  'connect.failed': 'Autho could not finish this: {detail}',
  'connect.tryAgain': 'Start again',

  'nav.counterparties': 'Customers and suppliers',
  'counterparties.title': 'Customers and suppliers',
  'counterparties.needsOneCompany':
    'Choose one company. Two ledgers can keep two currencies, and one list would add them together.',
  'counterparties.noLedger':
    'No accounting system is connected for this company, so Autho does not know who it trades with.',
  'counterparties.noSubledger':
    'This ledger marks no account as a customer or supplier subledger, so Autho cannot tell what is owed. The figures below are zero because nothing said which accounts are receivables — not because nobody owes anything.',
  'counterparties.empty': 'No customers or suppliers in this ledger yet.',
  'counterparties.filter': 'Show',
  'counterparties.filter.all': 'All',
  'counterparties.filter.customer': 'Customers',
  'counterparties.filter.supplier': 'Suppliers',
  'counterparties.role.customer': 'Customer',
  'counterparties.role.supplier': 'Supplier',
  'counterparties.role.other': 'Other',
  'counterparties.receivable': 'They owe us',
  'counterparties.payable': 'We owe them',
  'counterparties.behind':
    '{lines, plural, one {# line} other {# lines}} across {vouchers, plural, one {# voucher} other {# vouchers}}',
  'counterparties.ageing.title': 'Open items by age, {date}',
  'counterparties.ageing.receivables': 'Receivables',
  'counterparties.ageing.payables': 'Payables',
  'counterparties.ageing.carriedIn': 'Carried in',
  'counterparties.ageing.notDue': 'Not due',
  'counterparties.ageing.days0to30': '0–30 days',
  'counterparties.ageing.days31to60': '31–60 days',
  'counterparties.ageing.days61to90': '61–90 days',
  'counterparties.ageing.over90': 'Over 90 days',
  'counterparties.ageing.inCredit': 'In credit',
  'counterparties.ageing.net': 'Total',
  'counterparties.ageing.oldest': 'oldest open {date}',
  'counterparties.ageing.notNamed':
    'Includes {parties, plural, one {# party} other {# parties}} with no organisation number. They may be people, so they are counted here and named nowhere.',
  'counterparties.ageing.unattributed':
    'Posted to the receivable and payable accounts against no one, and not aged: {receivable} receivable, {payable} payable.',
  'forecast.title': 'Cash, the next 13 weeks',
  'forecast.needsOneCompany': 'Choose one company to see where its cash is going.',
  'forecast.from.books': 'From {cash} in the books on {date}.',
  'forecast.from.bank': 'From {cash} in the bank on {date}.',
  'forecast.from.statement': 'From {cash} on the bank statement of {date}.',
  'forecast.week': 'Week',
  'forecast.in': 'In from customers',
  'forecast.out': 'Out to suppliers',
  'forecast.other': 'Everything else',
  'forecast.closing': 'Cash at week end',
  'forecast.range': 'Range',
  'forecast.timingUnknown':
    'Not placed in any week, because nothing in the books says when they pay: {receivable} owed to the company, {payable} owed by it.',
  'forecast.beyond': 'Expected after the last week: {receivable} in, {payable} out.',
  'forecast.basis':
    'A forecast, not a record. Open invoices are placed by when each customer usually pays, and bills by when each supplier is usually paid. Everything else — payroll, rent, taxes — is the recent weekly average, so tax is not on its date. The range is the slow and the quick cases, added up week by week.',
  'forecast.withheld.no_books': 'There are no postings in the books to forecast from.',
  'forecast.withheld.books_stale':
    'The books run to {date}. A forecast from them would start in the past, so none is shown until they are current.',
  'forecast.withheld.cash_unknown':
    'Cash in the books is not known — an opening balance nobody has read, or no bank account — so there is nothing to start from.',
  'forecast.withheld.cash_other_currency':
    'The cash this company holds is in another currency than its books, so a forecast would add one to the other. None is given.',
  'forecast.withheld.history_short':
    'Only {weeks, plural, one {# week} other {# weeks}} of books so far. Fewer than four is not an average, so no forecast is shown yet.',
  'counterparties.ageing.basis':
    'Aged from the posting date: no line in these books states a due date, so this does not say what is overdue. A payment settles the invoice carrying its KID or reference first, and otherwise the oldest invoice. What was carried into the year has no date of its own.',
  'counterparties.ageing.basisDue':
    'Overdue from each invoice’s due date, as the books state it, and not due until the day after. A payment settles the invoice carrying its KID or reference first, and otherwise the oldest invoice. What was carried into the year has no date of its own.',
  'counterparties.ageing.basisMixed':
    'Overdue from the due date where the books state one ({due, plural, one {# open invoice} other {# open invoices}}), and aged from the posting date where they do not ({posting, plural, one {# invoice} other {# invoices}}). A payment settles the invoice carrying its KID or reference first, and otherwise the oldest invoice. What was carried into the year has no date of its own.',
  'counterparties.ageing.atBooksEnd':
    'Aged at {date}, the last day these books cover. They end in an earlier year, and ageing them at today would call every open invoice old.',

  'nav.documents': 'Documents',
  'documents.title': 'Documents',
  'documents.empty':
    'No documents yet. Drop a file here, or choose one, and Autho will read what it can.',
  'documents.drop': 'Drop a file here',
  'documents.or': 'or',
  'documents.choose': 'Choose a file',
  'documents.company': 'Company',
  'documents.accepted':
    'What Autho can read: PDFs with selectable text, plain text, and EHF or PEPPOL invoices. Up to {mb} MB.',
  'documents.uploading': 'Reading {filename}…',
  'documents.added': '{filename} added',
  'documents.alreadyHeld': '{filename} was already here, so nothing changed',
  'documents.refused': '{filename} was not added: {because}',
  'documents.needsOneCompany':
    'Select a single company to add a document. A document belongs to one company.',
  'documents.open': 'Open',
  'documents.erase': 'Erase',
  'documents.eraseTitle': 'Erase this document?',
  'documents.eraseExplain':
    'The file, the text read from it and everything derived from it are removed. This cannot be undone.',
  'documents.basis': 'On what basis',
  'documents.basisHint': 'For example: an erasure request from the person named in it.',
  'documents.eraseConfirm': 'Erase it',
  'documents.cancel': 'Cancel',
  'documents.erased': 'Erased',
  'documents.status.pending': 'Waiting to be read',
  'documents.status.extracted': 'Read',
  'documents.status.failed': 'Could not be read',
  'documents.status.deferred': 'Waiting for capacity',
  'documents.ownerOnly': 'Only the owner can erase a document.',
  'clarify.title': 'Clarify',
  'clarify.empty':
    'Nothing to clarify. Conflicts, gaps and stale facts will appear here, most valuable first.',
  'clarify.kind.merge': 'Possible duplicate',
  'clarify.kind.conflict': 'Sources disagree',
  'clarify.kind.gap': 'Missing information',
  'clarify.kind.classification': 'Needs a classification',
  'clarify.kind.staleness': 'May be out of date',
  'clarify.kind.enrichment': 'Enrichment available',
  'clarify.kind.unreadable': "Couldn't read this document",
  'clarify.financialYear.question':
    'When does the financial year of {orgnr} start? {file} does not say which accounting period its entries are in, so the books are read as the calendar year until a file that says so arrives. Export it again with accounting periods.',
  'clarify.financialYear.aFile': 'The uploaded file',
  'clarify.payoff': 'Resolving this links {count, plural, one {# record} other {# records}}',
  'clarify.keepCurrent': 'Keep current value',
  'clarify.useIncoming': 'Use {source} value',
  'clarify.skip': 'Skip',
  'clarify.resolved': 'Resolved',

  'audit.title': 'Audit log',
  'audit.description': 'Append-only. Every action and sync is recorded here and cannot be edited.',
  'audit.empty': 'No events match these filters.',
  'audit.column.time': 'Time',
  'audit.column.kind': 'Event',
  'audit.column.consumer': 'By',
  'audit.column.entities': 'Entities',
  'audit.column.outcome': 'Outcome',
  'audit.column.detail': 'Detail',
  'audit.filter.kind': 'Event type',
  'audit.filter.outcome': 'Outcome',
  'audit.filter.company': 'Company',
  'audit.filter.wholeProfile': 'Whole profile',
  'audit.filter.entity': 'Entity',
  'audit.filter.from': 'From',
  'audit.filter.to': 'To',
  'audit.chain.ok': 'Integrity check passed for {count, plural, one {# event} other {# events}}.',
  'audit.chain.broken': 'Integrity check failed at event {seq}. Contact support.',
  'audit.withheld':
    '{count, plural, one {# earlier event is visible only to the person it concerns.} other {# earlier events are visible only to the person they concern.}}',
  'audit.kind.read': 'Read',
  'audit.kind.policy_decision': 'Policy decision',
  'audit.kind.action': 'Action',
  'audit.kind.approval': 'Approval',
  'audit.kind.grant_change': 'Grant change',
  'audit.kind.policy_change': 'Policy change',
  'audit.kind.connection_change': 'Connection change',
  'audit.kind.federated_read': 'Federated read',
  'audit.kind.record_change': 'Record change',
  'audit.kind.membership_change': 'Membership change',
  'audit.kind.sync': 'Sync',
  'audit.kind.clarify_resolution': 'Clarify resolution',
  'audit.outcome.success': 'Success',
  'audit.outcome.failure': 'Failure',
  'audit.outcome.allow': 'Allowed',
  'audit.outcome.allow_with_approval': 'Needs approval',
  'audit.outcome.deny': 'Denied',
  'audit.consumer.user': 'Person',
  'audit.consumer.agent': 'Agent',
  'audit.consumer.system': 'System',
  'audit.singleControl': 'Single control',
  'audit.consumer.you': 'You',
  'audit.export.csv': 'Export CSV',
  'audit.export.json': 'Export JSON',

  'configure.title': 'Engine',
  'configure.description':
    'What Autho runs on, and how it is doing. What it reads from is under Sources.',
  'configure.connections.description': 'Only providers Autho can actually connect are listed.',
  'configure.status.pending': 'Not verified',
  'configure.status.live': 'Live',
  'configure.status.error': 'Failing',
  'configure.status.revoked': 'Revoked',
  'configure.lastSuccess': 'Last success {ago}',
  'configure.lastAttempt': 'Last attempt {ago}',
  'configure.never': 'Never',
  'configure.entities': '{count, plural, =0 {No entities} one {# entity} other {# entities}}',
  'configure.jurisdiction': 'Jurisdiction: {code}',
  'configure.mechanism.open': 'Open public API, no credentials',
  'configure.verify': 'Verify now',
  'configure.provider.brreg.name': 'Brønnøysundregistrene',
  'configure.provider.brreg.description':
    'Enhetsregisteret, roles, sub-units and filed accounts. Free and open (NLOD).',

  // **Provider apps** (D57): the paste that replaces a terminal. The sealed line is the reassurance that
  // matters â the secret is gone from sight the moment it lands, and that is the design, not a bug.
  'settings.providerApps': 'Provider apps',
  'settings.providerApps.explain':
    'One-time registrations other providers hand back when Autho is registered as an app. Paste them here; secrets are sealed with the tenant key the moment they land and are never shown again.',
  'settings.providerApps.todoist.where':
    'From Todoist: Settings → Integrations → Developer → App Management Console → your app. Redirect URL: https://autho.no/connect/todoist/callback',
  'settings.providerApps.todoist.configured': 'Registered as {clientId}.',
  'settings.providerApps.todoist.connected': 'connected',
  'settings.providerApps.todoist': 'Todoist',
  'settings.providerApps.clientId': 'Client ID',
  'settings.providerApps.clientSecret': 'Client Secret (sealed on save, never shown again)',
  'settings.providerApps.save': 'Save',
  'settings.providerApps.sealed': 'Sealed and stored. The secret will not be shown again.',
  'settings.title': 'Settings',
  'settings.language': 'Language',
  // The screen switches once the choice is saved (F344): said beside the control until then.
  'settings.language.saving': 'Saving…',
  'settings.language.notSaved':
    'The language you chose could not be saved, so the screen stays in the one it was in.',
  'settings.theme': 'Theme',
  'settings.theme.system': 'System',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',

  'palette.placeholder': 'Go to entity or screen…',
  'palette.empty': 'No matches',
  'palette.group.screens': 'Screens',
  'palette.group.entities': 'Entities',
  'palette.shortcut': '⌘K',

  'time.justNow': 'just now',

  // ── Ledger sync runs (M1): where a person reads the figures the log leaves out on purpose.
  'entity.ledger.open': 'Ledger sync runs',
  'ledger.title': 'Ledger',
  'ledger.description': 'Sync runs against the accounting system, and the crossing each one made.',
  'ledger.runs.empty': 'No sync runs yet.',
  'ledger.needsYou.title': 'Needs you: the books disagree with the accounting system',
  'ledger.needsYou.body':
    'The last sync wrote the ledger, and over its window ({from} to {to}) {count} account(s) moved differently here than the accounting system says. Nothing has been changed to make them agree.',
  'ledger.boundary.title': 'Needs you: the uploaded books do not meet the accounting system',
  'ledger.boundary.body':
    'The uploaded files close {lastFileYear}, and the connected accounting system opens {boundaryYear}. {count} balance-sheet account(s) close in the files differently from how they open in the system. Nothing has been changed to make them meet.',
  'ledger.boundary.file': 'Files, closing',
  'ledger.boundary.connected': 'System, opening',
  'ledger.boundary.unknown': 'Not known',
  'ledger.column.started': 'Started',
  'ledger.column.mode': 'Mode',
  'ledger.column.status': 'Status',
  'ledger.column.window': 'Window',
  'ledger.column.read': 'Read',
  'ledger.column.written': 'Written',
  'ledger.column.crossing': 'Crossing',
  'ledger.column.account': 'Account',
  'ledger.column.measure': 'Measure',
  'ledger.column.ours': 'Autho',
  'ledger.column.theirs': 'Source',
  'ledger.column.filed': 'Filed',
  'ledger.column.difference': 'Difference',
  'ledger.mode.dry': 'Dry run',
  'ledger.mode.probe': 'Probe',
  'ledger.mode.write': 'Write',
  'ledger.mode.unknown': '–',
  'ledger.status.running': 'Running',
  'ledger.status.succeeded': 'Succeeded',
  'ledger.status.failed': 'Failed',
  'ledger.status.abandoned': 'Kept nothing (probe)',
  'ledger.crossing.source': 'Against the source',
  'ledger.crossing.filing': 'Against the accounts filed with the register',
  'ledger.crossing.matches': 'Matches across {accounts} accounts',
  'ledger.crossing.differs': '{count} of {accounts} accounts differ',
  'ledger.crossing.filingMatches': 'All {measures} measures match',
  'ledger.crossing.filingDiffers': '{count} of {measures} measures differ',
  'ledger.crossing.none': 'No crossing on this run.',
  'ledger.crossing.postings':
    'Postings by id: {reported} reported, {placed} placed{neverRead, plural, =0 {} one {, # reported but never read} other {, # reported but never read}}{notReported, plural, =0 {} one {, # placed but not reported} other {, # placed but not reported}}',
  'ledger.crossing.explain':
    'Autho computes the trial balance from its own rows; the source reports its closing balances; the register holds what was filed. A difference is shown to the minor unit.',
  'ledger.error': 'Error',
  'ledger.measure.totalLiabilities': 'Total liabilities',
  'ledger.crossing.atPrecision': 'Compared at the precision the filing states: units of {unit}.',
  // D113 on the nightly's own run: what the comparison with the filing rests on, in words.
  'ledger.crossing.filingFromYearEnd':
    'No postings in Autho for the year: compared from the year-end balances stated on {date}, no postings.',
  'ledger.crossing.filingNothing':
    'No postings in Autho for the year and no balances stated at its end: not compared, and nothing differs.',
  'ledger.crossing.filingMissing': 'Not compared, for want of data: {measures}.',
  'ledger.wouldHaveWritten': '{count} (would have)',
  'ledger.reconciliation.title': 'Reconciled: everything read is placed or named',
  'ledger.reconciliation.vouchers':
    '{read} vouchers read = {placed} placed{opening, select, true { + the opening voucher} other {}}',
  'ledger.reconciliation.postings':
    '{read} postings read = {lines} lines + {opening} opening postings + {orphans} without a voucher + {unknown} with an unknown voucher',
  'ledger.reconciliation.opening':
    '{postings} opening postings → {balances} balances{summed, plural, =0 {} one { (# summed into an account already opened)} other { (# summed into accounts already opened)}}',
  // D112: the opening set placed whole, as a line per posting of its stated year-end balances.
  'ledger.reconciliation.openingLines':
    'Opening balance: {postings, plural, one {# posting} other {# postings}}, placed as {lines, plural, one {# line} other {# lines}} of its year-end balances',
  // Configure → Agents (M1.5): three lifetimes kept apart, and a secret shown once.
  'beliefs.title': 'Beliefs',
  'beliefs.description':
    'What this company has told Autho about itself, kept apart from what the sources say.',
  'beliefs.description.own':
    'What you have told Autho about yourself and how you want things done, kept apart from what your accounts say.',
  'beliefs.write.title': 'Write a belief',
  'beliefs.write.needsOneEntity':
    'Select a single entity to write a belief. A belief belongs to one company.',
  'beliefs.write.statement': 'What is true here',
  'beliefs.write.type': 'Kind',
  'beliefs.write.save': 'Record',
  'beliefs.write.saveCorrection': 'Record the correction',
  'beliefs.write.correcting': 'Correcting: “{statement}”',
  'beliefs.write.cancelCorrection': 'Write a new one instead',
  'beliefs.write.attribution':
    'Recorded as stated by you, at the highest confidence Autho keeps: a person said it.',
  'beliefs.list.title': 'What Autho believes',
  'beliefs.list.needsOneEntity': 'Select a single entity to read its beliefs.',
  'beliefs.list.empty': 'Nothing recorded yet.',
  'beliefs.list.showForgotten': 'Show forgotten',
  'beliefs.list.version': 'version {version}',
  'beliefs.list.forgotten': 'Forgotten',
  'beliefs.list.erased': 'The content of this belief has been erased. The record remains.',
  'beliefs.list.correct': 'Correct',
  'beliefs.list.forget': 'Forget',
  'beliefs.list.forgetIsNotErase':
    'Forgetting stops a belief being used, at every point in time. It does not rewrite history: the record stays, and so does the audit trail.',
  'approvals.title': 'Approvals',
  'approvals.description':
    'What agents ask to do here, what waits for a person, and the switch that stops them.',
  'approvals.onlyAdmins':
    'Only an owner or admin of this profile can stop agents, approve or change a rule.',
  'approvals.halt.title': 'Kill switch',
  'approvals.halt.all':
    'Autho has stopped every agent action on its own side. Nothing here can lift that.',
  'approvals.halt.reason': 'Why (kept with the click)',
  'approvals.halt.everything': 'Every company in this profile',
  'approvals.halt.stopped': 'Stopped',
  'approvals.halt.running': 'Agents may act',
  'approvals.halt.lifts': '{done} of {needed} lifts',
  'approvals.halt.stop': 'Stop agents',
  'approvals.halt.whyFirst': 'Write why first: Stop agents and Lift wait for it.',
  'approvals.halt.lift': 'Lift',
  'approvals.halt.counting':
    'One click stops agents at once. Lifting takes one admin where the profile has one, and two different admins where it has more.',
  'approvals.waiting.title': 'Waiting for a person',
  'approvals.waiting.empty': 'Nothing is waiting.',
  'approvals.signInAgain':
    'Approving needs a sign-in from the last five minutes, so an open session alone cannot say yes. Sign in again, then approve.',
  'approvals.signInAgain.button': 'Sign in again',
  'approvals.waiting.recheck':
    'An approved action is checked again before it runs: if the kill switch, the rule or the grant has changed since, it is voided and nothing is written.',
  'approvals.item.asks': '{agent} asks to {action} about {entity}',
  'approvals.item.grant': 'under the grant “{purpose}”',
  'approvals.item.expires': 'expires {when}',
  'approvals.item.approve': 'Approve',
  'approvals.item.reject': 'Reject',
  'approvals.action.remember': 'leave a note',
  'approvals.reason.over_approval_threshold': 'over the approval threshold',
  'approvals.reason.over_dual_control_threshold': 'over the two-person threshold',
  'approvals.reason.no_exchange_rate': 'no exchange rate for the day',
  'approvals.reason.total_unknown': 'the running total could not be read',
  'approvals.reason.new_counterparty': 'a new counterparty',
  'approvals.reason.no_contract_or_po': 'no contract or purchase order',
  'approvals.reason.low_confidence': 'the agent is not sure enough',
  'approvals.status.waiting': 'Waiting',
  'approvals.status.approved': 'Approved',
  'approvals.status.rejected': 'Rejected',
  'approvals.status.expired': 'Expired',
  'approvals.status.void': 'Voided',
  'approvals.status.executed': 'Done',
  'approvals.rules.title': 'Rules',
  'approvals.rules.remember':
    'Leave a note: an agent with a grant for it may write a note about a company. Notes are always shown as an agent’s note, never as something the company stated. Below the floor, a person approves the note first.',
  'approvals.rules.none': 'No rule is switched on, so agents can only read.',
  'approvals.rules.on': 'On',
  'approvals.rules.waiting': 'Waiting for a second admin ({done} of 2)',
  'approvals.rules.floor': 'floor {floor}',
  'approvals.rules.singleControl': 'decided by one person',
  'approvals.rules.approve': 'Approve',
  'approvals.rules.off': 'Switch off',
  'approvals.rules.floorInput': 'Floor (0 to 1)',
  'approvals.rules.switchOn': 'Switch on',
  'approvals.rules.oneAdmin':
    'This profile has one admin: a rule switched on here is on at once, marked as decided by one person.',
  'approvals.rules.twoAdmins':
    'Switching a rule on or changing it waits for a second admin. Switching it off is at once.',
  'beliefs.list.agentsNote': "An agent's note",
  'beliefs.list.eraseNote': 'Erase note',
  'beliefs.type.fact': 'Fact',
  'beliefs.type.preference': 'Preference',
  'beliefs.type.pattern': 'Pattern',
  'beliefs.type.instruction': 'Instruction',
  'beliefs.type.decision': 'Decision',
  'beliefs.type.convention': 'Convention',
  'beliefs.type.constraint': 'Constraint',
  'beliefs.type.correction': 'Correction',
  'beliefs.confidence.stated': 'Stated',
  'beliefs.confidence.confirmed': 'Confirmed',
  'beliefs.confidence.extracted': 'Extracted',
  'beliefs.confidence.inferred': 'Inferred',
  'agents.title': 'Agents',
  'agents.description': 'What may read this group, under which grant, and until when.',
  'agents.none': 'No agent has been created yet.',
  'agents.never': 'never',
  'agents.expires': 'expires',
  'agents.revoked': 'revoked',
  'agents.enable': 'Enable',
  'agents.disable': 'Disable',
  'agents.revoke': 'Revoke',
  'agents.status.enabled': 'Enabled',
  'agents.status.disabled': 'Disabled — every read is denied',
  'agents.status.revoked': 'Revoked',
  'agents.create.title': 'New agent',
  'agents.create.submit': 'Create',
  'agents.create.starts_disabled':
    'An agent is created disabled and reads nothing until you enable it and give it a grant.',
  'agents.field.name': 'Name',
  'agents.field.version': 'Version',
  'agents.secret.title': 'The credential, shown once',
  'agents.secret.once':
    'Copy it into the agent now. Autho stores only a hash of it and can never show it again.',
  'agents.secret.dismiss': 'I have copied it',
  'agents.credentials.title': 'Credentials',
  'agents.credentials.issue': 'Issue credential',
  'agents.credentials.none': 'No credential has been issued.',
  'agents.credentials.folded': '{count} expired or revoked credential(s), kept for the record',
  'agents.credentials.revokedOn': 'revoked {date}',
  'agents.credentials.expiredOn': 'expired {date}',
  'agents.credentials.prefix': 'Prefix',
  'agents.credentials.last_used': 'Last used to read',
  'agents.credentials.actions': 'Actions',
  'agents.grants.title': 'Grants',
  'agents.grants.none': 'No grant. The agent authenticates and is denied everything.',
  'agents.grants.new': 'New grant',
  'agents.grants.entities': 'Entities',
  'agents.grants.accounts': 'Connected accounts',
  'agents.grants.profile': 'This profile',
  'agents.grants.this_profile': 'Your own profile',
  'agents.grants.readsBeliefs':
    'me.beliefs lets this agent read what you have told Autho about your own life — your conventions, preferences, decisions and instructions, each with who stated it and when. It reads them and nothing else: it cannot record, correct or forget one. No other word here includes it.',
  'agents.grants.readsContents':
    'me.mail.read lets this agent read what a message says — the text of it — and not only that it arrived. Every other resource here gives it pointers: a subject, who took part, a figure. Nothing is stored when it reads one; the message is fetched from the provider at that moment and the audit log records which message, under which grant.',
  'agents.grants.scope_hint':
    'Tick what it may read first: that decides whether this grant names companies or accounts.',
  'agents.grants.scope_hint_me':
    'Tick what it may read first: that decides whether this grant names connected accounts or this profile.',
  'agents.grants.scope_mixed':
    'A grant names one kind of thing. Company resources are granted per company, mail, calendar and files per connected account, and me.beliefs for this profile as a whole — make two grants.',
  'agents.grants.unreached':
    'This grant names {count, plural, one {one thing} other {# things}} it cannot read: {what}. It answers nothing for those.',
  'agents.grants.unreached_entity': 'a company, where this grant reads accounts',
  'agents.grants.unreached_account': 'an account, where this grant reads companies',
  'agents.grants.unreached_gone': 'an account that is no longer connected',
  'agents.grants.kind_org': 'Per company',
  'agents.grants.kind_me': 'Per connected account',
  'agents.grants.kind_profile': 'For this profile',
  'agents.grants.resources': 'Resources',
  'agents.grants.purpose': 'Purpose: {purpose} — recorded, not enforced',
  'agents.grants.purpose_label': 'Purpose, in words (at least 8 characters)',
  'agents.grants.scope': '{count, plural, one {# entity} other {# entities}}',
  'agents.grants.days': 'Days',
  'agents.grants.submit': 'Grant',
  'agents.grants.expiry_note':
    'Every grant expires; at most 90 days. Renewing is a new grant, never an extension.',
  'agents.grant_status.active': 'active',
  'agents.grant_status.expired': 'expired',
  'agents.grant_status.revoked': 'revoked',
  'audit.kind.credential_change': 'Credential change',
  'audit.kind.agent_change': 'Agent change',
  'audit.underGrant': 'under grant {id}',
  'nav.profile': 'Profile',
  'nav.finances': 'Finances',
  'nav.goals': 'Goals',
  'goals.title': 'Goals',
  'goals.description':
    'What you are working towards, and what each goal is waiting on. You type a goal; Autho never makes one.',
  'goals.empty':
    'No goals yet. Add one above: a title, why it matters, and the day you mean it by.',
  'goals.new': 'New goal',
  'goals.add': 'Add goal',
  'goals.suggestOnly': 'The assistant may suggest a goal. Only you add one.',
  'goals.field.title': 'Goal',
  'goals.field.why': 'Why',
  'goals.field.horizon': 'By',
  'goals.field.state': 'State',
  'goals.state.active': 'Active',
  'goals.state.paused': 'Paused',
  'goals.state.achieved': 'Achieved',
  'goals.state.abandoned': 'Abandoned',
  'goals.by': 'by {date}',
  'goals.next': 'Next: {step}, due {date}',
  'goals.next.undated': 'Next: {step}, no date',
  'goals.next.none':
    'Nothing linked is open. Link a commitment or a task to give this goal a next step.',
  'goals.links': 'What this goal is waiting on',
  'goals.link.task': 'Todoist',
  'goals.link.commitment': 'From your mail',
  'goals.link.gone': 'no longer on your Todoist list',
  'goals.link.closed': 'done or forgotten',
  'goals.link.open': 'Open',
  'goals.link': 'Link',
  'goals.unlink': 'Unlink',
  'goals.linkCommitment': 'Link a commitment',
  'goals.choose': 'Choose…',
  'goals.edit': 'Edit',
  'goals.edit.close': 'Close',
  'goals.save': 'Save',
  'goals.error.title': 'Give the goal a title.',
  'goals.error.tooLong': 'That is too long.',
  'goals.error.horizon': 'That is not a date.',
  'goals.error.alreadyLinked': 'That is already linked to this goal.',
  'goals.todoist.title': 'Your Todoist list',
  'goals.todoist.explain':
    'Read from Todoist each time this page opens, and never stored. Autho reads your tasks and writes nothing to your list. Who and what a task names is a suggestion from the names Autho already holds.',
  'goals.todoist.not_connected': 'Your Todoist list is not connected.',
  'goals.todoist.registration': 'Connecting needs your Todoist app registration, pasted once under',
  'goals.todoist.failed':
    'Your Todoist list could not be read just now, so its tasks are not shown. Nothing has been changed.',
  'goals.todoist.paused': 'Your Todoist list is paused, so its tasks are not read.',
  'goals.todoist.read': 'Read from Todoist just now.',
  'goals.todoist.empty': 'Your Todoist list has no open tasks.',
  'goals.task.line': 'Task',
  'goals.task.due': 'Due',
  'goals.task.priority': 'Priority',
  'goals.task.names': 'Names',
  'goals.task.goal': 'Goal',
  'goals.task.suggested': 'Suggested from the task’s line; not a link you made',
  'goals.task.kind.person': 'Person',
  'goals.task.kind.organisation': 'Organisation',
  'goals.task.kind.asset': 'Asset',
  'goals.task.linkTo': 'Link “{task}” to a goal',
  'inbox.goal': 'A step towards: {goal}',
  'inbox.goalTasks.not_connected':
    'A goal links a Todoist task, and your Todoist list is not connected, so its tasks are not on this list.',
  'inbox.goalTasks.paused':
    'Your Todoist list is paused, so a goal’s task that is due is not on this list.',
  'inbox.goalTasks.failed':
    'Your Todoist list could not be read just now, so a goal’s task that is due may be missing from this list.',
  'accounts.todoist.goals': 'Your Todoist list, for Goals',
  'accounts.todoist.goals.explain':
    'Autho reads your open tasks when you open Goals or the Inbox, and never stores them. The grant only lets Autho read your tasks: it writes nothing to your list, and a token that cannot read is refused and withdrawn.',
  'todoist.callback.connected.goals':
    'Connected. Autho can read your Todoist list for your Goals, and writes nothing to it.',
  'todoist.callback.cannotRead':
    'Todoist handed back a token that cannot read your list, so nothing was kept and the token was withdrawn.',
  'settings.providerApps.explain.me':
    'Your own registrations: Todoist’s, for reading your list into Goals, and Enable Banking’s application id, for your own bank accounts. Paste them here; a secret is sealed the moment it lands and is never shown again.',
  'profile.title': 'Profile',
  'profile.description': 'Who you are, as you typed it. Nothing here is read from an account.',
  'profile.field.fullName': 'Name',
  'profile.field.birthDate': 'Date of birth',
  'profile.field.address': 'Address',
  'profile.field.household': 'Household',
  'profile.updated': 'Last changed {when}',
  'profile.empty': 'Nothing typed yet.',
  'profile.save': 'Save',
  'profile.saved': 'Saved.',
  'profile.error.birthDate': 'That date of birth is not a date between 1900 and today.',
  'profile.error.tooLong': 'One of the fields is too long.',
  'finances.title': 'Finances',
  'finances.description':
    'What you own and what you owe, as you typed it. A new value keeps the old one as history; a value in another currency stays in that currency.',
  'finances.provenance': 'Everything on this page is stated by you.',
  'finances.totals': 'Totals',
  'finances.totals.none': 'Nothing has a value yet, so there is nothing to add up.',
  'finances.totals.assets': 'Assets',
  'finances.totals.liabilities': 'Liabilities',
  'finances.totals.bank': 'In your bank',
  'finances.bank': 'Bank accounts',
  'finances.bank.explain':
    'Your own accounts, connected with your own consent at your bank and read when this page opens. Balances and transactions are read from the bank and never stored. Autho reads and never pays, moves or initiates anything.',
  'finances.bank.none': 'No bank is connected.',
  'finances.bank.connect': 'Connect a bank',
  'finances.bank.pick': 'Your bank',
  'finances.bank.connect.go': 'Continue to the bank',
  'finances.bank.connect.explain':
    'You give your consent with BankID in your bank’s own pages. It lets Autho read your balances and transactions until it lapses, and nothing else.',
  'finances.bank.registration':
    'Connecting needs the Enable Banking application id, pasted once under',
  'finances.bank.account': 'Account',
  'finances.bank.balance': 'Balance',
  'finances.bank.consent': 'Consent',
  'finances.bank.asOf': 'as of {date}',
  'finances.bank.until': 'until {date}',
  'finances.bank.lines': 'Income and payments that repeat',
  'finances.bank.lines.explain':
    'Read from your transactions: the same counterpart at least three times on a regular rhythm. Only a summary of each line is kept — the counterpart, the rhythm, the usual amount and which transactions it was read from — never the transactions themselves. Each is a suggestion until you confirm it or forget it.',
  'finances.bank.lines.none': 'Nothing repeats yet in what the bank has given.',
  'finances.bank.direction': 'Direction',
  'finances.bank.counterpart': 'Counterpart',
  'finances.bank.cadence': 'Rhythm',
  'finances.bank.usual': 'Usual amount',
  'finances.bank.next': 'Next expected',
  'finances.bank.verdict': 'Your verdict',
  'finances.bank.in': 'Income',
  'finances.bank.out': 'Payment',
  'finances.bank.basis': 'The transactions it was read from',
  'finances.bank.basis.unread': 'not read just now',
  'finances.bank.cadence.weekly': 'Weekly',
  'finances.bank.cadence.monthly': 'Monthly',
  'finances.bank.cadence.quarterly': 'Quarterly',
  'finances.bank.cadence.yearly': 'Yearly',
  'finances.bank.state.proposed': 'Suggested',
  'finances.bank.state.confirmed': 'Confirmed',
  'finances.bank.state.forgotten': 'Forgotten',
  'finances.bank.kind': 'What “{line}” is',
  'finances.bank.kind.none': 'Not said',
  'finances.bank.kind.salary': 'Salary',
  'finances.bank.kind.rent': 'Rent',
  'finances.bank.kind.subscription': 'Subscription',
  'finances.bank.kind.loan': 'Loan',
  'finances.bank.kind.other': 'Other',
  'finances.bank.confirm': 'Confirm',
  'finances.bank.forget': 'Forget',
  'finances.totals.net': 'Net',
  'finances.totals.notConverted':
    'One line per currency: Autho does not convert one currency into another.',
  'finances.assets': 'Assets',
  'finances.assets.add': 'Add an asset',
  'finances.assets.addSubmit': 'Add asset',
  'finances.assets.empty':
    'No assets yet. Add one by its type: what it is, your stake in it, and how involved you are.',
  'finances.liabilities': 'Liabilities',
  'finances.liabilities.add': 'Add a liability',
  'finances.liabilities.addSubmit': 'Add liability',
  'finances.liabilities.empty': 'No liabilities yet.',
  'finances.step.type': 'Type',
  'finances.step.stake': 'Stake',
  'finances.step.value': 'Value (optional)',
  'finances.type.class': 'Class',
  'finances.type.subclass': 'Subclass',
  'finances.type.vehicle': 'Vehicle',
  'finances.type.choose': 'Choose…',
  'finances.name': 'Name',
  'finances.notes': 'Notes',
  'finances.save': 'Save',
  'finances.cancel': 'Cancel',
  'finances.edit': 'Edit',
  'finances.edit.title': 'Edit {name}',
  'finances.stake.shares': 'Shares',
  'finances.stake.units': 'Units',
  'finances.stake.face': 'Face value',
  'finances.stake.quantity': 'Quantity',
  'finances.stake.unique': 'One item',
  'finances.stake.none': 'Nothing to say here: the value is the holding.',
  'finances.stake.field.shareClass': 'Share class',
  'finances.stake.field.outstanding': 'Shares outstanding',
  'finances.stake.field.percent': 'Share, in per cent',
  'finances.stake.field.rate': 'Interest, % a year',
  'finances.stake.field.maturity': 'Falls due',
  'finances.stake.shown.shares': '{shares} shares',
  'finances.stake.shown.units': '{units} units',
  'finances.stake.shown.face': '{face} face value',
  'finances.stake.shown.quantity': '{quantity}',
  'finances.band.financial': 'Financial interest',
  'finances.band.significant': 'Significant influence',
  'finances.band.joint': 'Joint control',
  'finances.band.majority': 'Majority',
  'finances.band.wholly': 'Wholly owned',
  'finances.involvement': 'Involvement',
  'finances.involvement.passive': 'Passive',
  'finances.involvement.active': 'Active',
  'finances.involvement.operating': 'Operating',
  'finances.value.amount': 'Amount',
  'finances.value.currency': 'Currency',
  'finances.value.asOf': 'As of',
  'finances.value.new': 'New value',
  'finances.value.newBalance': 'New balance',
  'finances.value.save': 'Save value',
  'finances.noValue': 'No value yet',
  'finances.asOf': 'as of {day}',
  'finances.history': '{count, plural, one {# earlier value} other {# earlier values}}',
  'finances.column.name': 'Name',
  'finances.column.type': 'Type',
  'finances.column.stake': 'Stake',
  'finances.column.involvement': 'Involvement',
  'finances.column.value': 'Value',
  'finances.column.acts': 'What you can do',
  'finances.ended': 'Ended, kept as history',
  'finances.endedOn': 'Ended {day}',
  'finances.paidOn': 'Paid off {day}',
  'finances.end.asset': 'End',
  'finances.end.liability': 'Paid off',
  'finances.end.asset.on': 'Ended on',
  'finances.end.liability.on': 'Paid off on',
  'finances.end.asset.confirm': 'End it',
  'finances.end.liability.confirm': 'Mark paid off',
  'finances.end.kept': 'It stays here as history and no longer changes.',
  'finances.liability.kind': 'Kind',
  'finances.liability.kind.loan': 'Loan',
  'finances.liability.kind.credit_card': 'Credit card',
  'finances.liability.kind.other': 'Other',
  'finances.liability.lender': 'Lender',
  'finances.liability.balance': 'Balance',
  'finances.error.stake_required': 'Say the stake this type asks for.',
  'finances.error.stake_not_a_number': 'That stake is not a number.',
  'finances.error.stake_out_of_range': 'That stake is outside what it can be.',
  'finances.error.outstanding_below_shares':
    'Shares outstanding cannot be fewer than the shares you hold.',
  'finances.error.involvement_required': 'Say whether you are passive, active or operating.',
  'finances.error.name_required': 'Give it a name.',
  'finances.error.text_too_long': 'That text is too long.',
  'finances.error.invalid_date': 'That is not a date.',
  'finances.error.date_in_future': 'That date has not come yet.',
  'finances.error.currency_unknown': 'That is not a currency code.',
  'finances.error.value_not_a_number': 'That value is not an amount with at most two decimals.',
  'finances.error.holding_ended': 'It has ended; it is kept as history and does not change.',
  'taxonomy.class.public_markets': 'Public markets',
  'taxonomy.class.private_equity': 'Private equity',
  'taxonomy.class.private_credit': 'Private credit',
  'taxonomy.class.real_assets': 'Real assets',
  'taxonomy.class.commodities': 'Commodities',
  'taxonomy.class.cash': 'Cash',
  'taxonomy.class.digital_assets': 'Digital assets',
  'taxonomy.class.pensions_insurance': 'Pensions & insurance',
  'taxonomy.class.collectibles': 'Collectibles & alternatives',
  'taxonomy.class.intellectual_property': 'Intellectual property',
  'taxonomy.subclass.stock': 'Stock',
  'taxonomy.subclass.bond': 'Bond',
  'taxonomy.subclass.fund': 'Fund',
  'taxonomy.subclass.etf': 'ETF',
  'taxonomy.subclass.reit': 'REIT',
  'taxonomy.subclass.derivatives': 'Derivatives & options',
  'taxonomy.subclass.venture_capital': 'Venture capital',
  'taxonomy.subclass.growth_equity': 'Growth equity',
  'taxonomy.subclass.buyout': 'Buyout',
  'taxonomy.subclass.private_company': 'Private company',
  'taxonomy.subclass.equity_compensation': 'Equity compensation',
  'taxonomy.subclass.direct_loan': 'Direct loan',
  'taxonomy.subclass.shareholder_loan': 'Shareholder loan',
  'taxonomy.subclass.convertible_note': 'Convertible note',
  'taxonomy.subclass.personal_loan': 'Personal loan / IOU',
  'taxonomy.subclass.residential': 'Real estate: residential',
  'taxonomy.subclass.commercial': 'Real estate: commercial',
  'taxonomy.subclass.land': 'Real estate: land',
  'taxonomy.subclass.infrastructure': 'Infrastructure',
  'taxonomy.subclass.natural_resources': 'Natural resources',
  'taxonomy.subclass.precious_metals': 'Precious metals',
  'taxonomy.subclass.industrial_metals': 'Industrial metals',
  'taxonomy.subclass.energy': 'Energy',
  'taxonomy.subclass.agriculture': 'Agriculture',
  'taxonomy.subclass.bank_deposit': 'Bank deposit',
  'taxonomy.subclass.physical_cash': 'Physical cash',
  'taxonomy.subclass.receivable': 'Receivable',
  'taxonomy.subclass.cryptocurrency': 'Cryptocurrency',
  'taxonomy.subclass.stablecoins': 'Stablecoins',
  'taxonomy.subclass.tokens': 'Tokens',
  'taxonomy.subclass.nfts': 'NFTs',
  'taxonomy.subclass.staked_defi': 'Staked / DeFi positions',
  'taxonomy.subclass.occupational_pension': 'Occupational pension (innskuddspensjon / OTP)',
  'taxonomy.subclass.individual_pension': 'Individual pension (IPS)',
  'taxonomy.subclass.pension_capital_certificate':
    'Pension capital certificate (pensjonskapitalbevis)',
  'taxonomy.subclass.paid_up_policy': 'Paid-up policy (fripolise)',
  'taxonomy.subclass.life_insurance': 'Life insurance with surrender value',
  'taxonomy.subclass.annuity': 'Annuity',
  'taxonomy.subclass.art': 'Art',
  'taxonomy.subclass.watches_jewelry': 'Watches & jewellery',
  'taxonomy.subclass.vehicles_boats_aircraft': 'Vehicles, boats & aircraft',
  'taxonomy.subclass.wine_spirits': 'Wine & spirits',
  'taxonomy.subclass.other': 'Other',
  'taxonomy.subclass.patents_technology': 'Patents & technology',
  'taxonomy.subclass.copyrights_media': 'Copyrights & media',
  'taxonomy.subclass.trademarks_brands': 'Trademarks & brands',
  'taxonomy.subclass.domain_names': 'Domain names',
  'taxonomy.subclass.royalties': 'Royalties',
  'taxonomy.vehicle.direct': 'Direct',
  'taxonomy.vehicle.secondary': 'Secondary',
  'taxonomy.vehicle.fund': 'Fund',
  'taxonomy.vehicle.operating_company': 'Operating company',
  'taxonomy.vehicle.holding_company': 'Holding company',
  'taxonomy.vehicle.sole_proprietorship': 'Sole proprietorship (ENK)',
  'taxonomy.vehicle.options': 'Options',
  'taxonomy.vehicle.warrants': 'Warrants',
  'taxonomy.vehicle.rsus': 'RSUs',
  'taxonomy.vehicle.phantom_shares': 'Phantom shares',
  'taxonomy.vehicle.primary_residence': 'Primary residence',
  'taxonomy.vehicle.rental_property': 'Rental property',
  'taxonomy.vehicle.holiday_property': 'Holiday property (hytte)',
  'taxonomy.vehicle.coop_share': 'Co-op share (borettslag)',
  'taxonomy.vehicle.office': 'Office',
  'taxonomy.vehicle.retail': 'Retail',
  'taxonomy.vehicle.industrial': 'Industrial',
  'taxonomy.vehicle.hospitality': 'Hospitality',
  'taxonomy.vehicle.development': 'Development',
  'taxonomy.vehicle.agricultural': 'Agricultural',
  'taxonomy.vehicle.forestry': 'Forestry / timberland',
  'taxonomy.vehicle.undeveloped': 'Undeveloped',
  'taxonomy.vehicle.energy': 'Energy',
  'taxonomy.vehicle.transportation': 'Transportation',
  'taxonomy.vehicle.utilities': 'Utilities',
  'taxonomy.vehicle.direct_ownership': 'Direct ownership',
  'taxonomy.vehicle.royalty_rights': 'Royalty / rights',
} as const;

export type MessageKey = keyof typeof en;
